import type React from "react";
import { toast } from "react-hot-toast";
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import Dropdown from "react-dropdown-select";
import { Plus, X, UploadCloud, ArrowRight, ArrowLeft, User, Briefcase, CheckCircle, MapPin, Phone, Mail, Calendar, FileText, Languages, Award, Target, Building, Clock, Loader2, Search, ChevronDown, Check, Eye, AlertTriangle } from 'lucide-react';
import { createApplication, type ICreateApplicationFormData, getJobCategoriesList, getCandidateById, getSkillsList, getToolsList } from "../../services/candidateapi";
import { createApproach, updateApproach, getApproachApiErrorMessage, isBlockedPhoneError } from "../../services/approachesapi";
import {  getJobsForSelection } from "../../services/jobsapi";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useParams } from "react-router-dom";
import { generateGeminiContentFromResume, uploadFile } from "../../services/usersapi";
import { ScheduleForm } from "../../components/ScheduleForm";
import { ScheduleFormData, ValidationErrors } from "../../components/types/application.types";
import { validateDate } from "../../components/utils/validation.utils";
import DatePicker from "react-datepicker";
import { format } from "date-fns"
import { useRbac } from "../../context";
import { isValidEmail } from "../../components/utils/application.utils";
import { scheduleInterviewRound } from "../../services/interviewapi";
import { MultiValue, StylesConfig } from "react-select";
import CreatableSelect from "react-select/creatable";
import BlockedPhonePopup from "../../components/BlockedPhonePopup";

// Job interface for API response
interface Job {
  _id: string;
  title: string;
  companyName: string;
  company: string;
  location: string;
  status: string;
  workType?: string;
  id?: string;
}

interface GetJobsResponse {
  jobs: Job[];
  totalJobs: number;
  totalPages: number;
  currentPage: number;
}

// Update the CandidateFormData interface to match backend expectations
interface CandidateFormData {
  // --- Job Details (Required) ---
  jobId: string;
  jobType: 'main' | 'sub';
  // --- Candidate Details (All required as per backend schema) ---
  name: string;
  email: string;
  phone: string;
  alternativePhoneNumber?: string;
  maritalStatus?: string;
  howOftenAbleToJoin?: string;
  fatherName: string;
  dateOfBirth: string; // ISO 8601 date string
  gender: 'Male' | 'Female' | 'Other';
  aadharNumber?: string;
  highestQualification: string;
  currentLocationDetails: {
    street: string;
    area: string;
    city: string;
    pincode: string;
    fullAddress: string;
  };
  spokenLanguages: string[];
  totalExperienceYears: number;
  totalExperienceMonths: number;
  skills: string[];
  knownTools: string[];
  preferredJobCategories: string[];
  preferredEmploymentTypes: string[];
  preferredWorkTypes: ('remote' | 'hybrid' | 'on-site')[];
}

interface AddCandidateFormProps {
  onBack: () => void;
  onSubmit?: (data: CandidateFormData) => void;
  refreshCandidates?: () => void;
}

export default function AddCandidateForm({ onBack, onSubmit, refreshCandidates }: AddCandidateFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const approachState = (location?.state as any) || {};
  const { id } = useParams();
  const apiClient = createApplication;
  const [currentStep, setCurrentStep] = useState(1);
  const formTopRef = useRef<HTMLDivElement>(null);
  const approachedFilled = useRef(false);
  const [formData, setFormData] = useState<CandidateFormData>({
    name: "",
    email: "",
    phone: "",
    alternativePhoneNumber: "",
    maritalStatus: "",
    howOftenAbleToJoin: "",
    fatherName: "",
    dateOfBirth: "",
    gender: "Male",
    aadharNumber: "",
    highestQualification: "",
    currentLocationDetails: {
      street: "",
      area: "",
      city: "",
      pincode: "",
      fullAddress: "",
    },
    spokenLanguages: [],
    totalExperienceYears: 0,
    totalExperienceMonths: 0,
    skills: [],
    knownTools: [],
    preferredJobCategories: [],
    preferredEmploymentTypes: [],
    preferredWorkTypes: [],
    jobId: "",
    jobType: "main",
    
  });

  // Fetch candidate details if editing
  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const candidate = await getCandidateById(id);
          console.log("candidate details", candidate);
          if (candidate) {
            setFormData({
              ...formData,
              ...candidate,
              currentLocationDetails: candidate.currentLocationDetails || formData.currentLocationDetails,
              skills: candidate.skills || [],
              knownTools: candidate.knownTools || [],
              spokenLanguages: candidate.spokenLanguages || [],
              preferredJobCategories: candidate.preferredJobCategories || [],
              preferredEmploymentTypes: candidate.preferredEmploymentTypes || [],
              preferredWorkTypes: candidate.preferredWorkTypes || [],
            });
          }
        } catch (error) {
          console.error("Failed to fetch candidate details", error);
        }
      })();
    }
  }, [id]);

  const { user } = useRbac()
  const [currentJobCategory, setCurrentJobCategory] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [resumeFileName, setResumeFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const performingUser = { email: Cookies.get("userEmail") || "Unknown" };
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [scheduleInterview, setScheduleInterview] = useState(false);
  const [blockedPopupOpen, setBlockedPopupOpen] = useState(false);
  const [blockedPopupMessage, setBlockedPopupMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    startTime: false,
    endTime: false,
    scheduledDate: false,
  })
  const [emailInput, setEmailInput] = useState<string>("")

  // Jobs state
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [jobSearchTerm, setJobSearchTerm] = useState("");

  // Searchable dropdown states
  const [openCategoryDropdown, setOpenCategoryDropdown] = useState(false);
  const [openEmploymentTypeDropdown, setOpenEmploymentTypeDropdown] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [scheduleFormData, setScheduleFormData] = useState<ScheduleFormData>({
    interviewType: "Offline",
    scheduledDate: "",
    startTime: "",
    endTime: "",
    timeZone: "UTC",
    interviewers: [],
    notes: "",
    additionalParticipants: [],
    location: "",
  })
  // Qualification options with modern names
  const qualificationOptions = [
    { value: "10th", label: "Secondary Education (10th Grade)" },
    { value: "12th", label: "Higher Secondary Education (12th Grade)" },
    { value: "ITI", label: "Industrial Training Institute (ITI)" },
    { value: "Diploma", label: "Diploma" },
    { value: "Graduation (10 + 2 + 3)", label: "Bachelor's Degree (3 Years)" },
    { value: "Graduation (10 + 2 + 4)", label: "Bachelor's Degree (4 Years)" },
    { value: "Post Graduation", label: "Master's Degree" },
    { value: "PhD", label: "Doctorate (PhD)" },
  ];
    const [resloading, setResloading] = useState<boolean>(false);
    const [uploadedURL, setUploadedURL] = useState<string | null>(null);
    const [UploadError,setUploadError]=useState(false);
    const [resumeUploadStep, setResumeUploadStep] = useState<'idle' | 'uploading' | 'extracting'>('idle');
      

      useEffect(() => {
        const fetchResume=async ()=>{
          setResloading(true);
          setUploadError(false)
    
          try{
            if (resumeFile && resumeFileName){
              setResumeUploadStep('uploading');
              toast.loading("Uploading resume...", { id: "resume-upload" });
              const response = await uploadFile(resumeFile, 'resumes');
              console.log("Upload response:", response);
              // Get fileUrl from response
              if (response?.fileUrl) {
                setUploadedURL(response.fileUrl);
                console.log("✅ Uploaded URL set to:", response.fileUrl);
                // Clear resume error when upload succeeds
                setErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors.resume;
                  return newErrors;
                });
              } else {
                console.error("❌ No fileUrl found in response:", response);
              }
              toast.success("Resume uploaded successfully!", { id: "resume-upload" });
            }
            if(resumeFile){
              setResumeUploadStep('extracting');
              toast.loading("Extracting data from resume...", { id: "resume-extract" });
              // const response =await generateGeminiContentFromResume(resumeFile);
              // console.log(response);
              // Only update specific fields from Gemini response, preserve existing form data
              // setFormData((prev) => {
              //   const updates: any = {};
                
              //   // Only update fields that exist in response and don't overwrite user-entered data
              //   if (response.data?.name && !prev.name?.trim()) {
              //     updates.name = response.data.name;
              //   }
              //   if (response.data?.email && !prev.email?.trim()) {
              //     updates.email = response.data.email;
              //   }
              //   if (response.data?.phone && !prev.phone?.trim()) {
              //     updates.phone = response.data.phone;
              //   }
              //   if (response.data?.fatherName && !prev.fatherName?.trim()) {
              //     updates.fatherName = response.data.fatherName;
              //   }
              //   if (response.data?.dateOfBirth && !prev.dateOfBirth) {
              //     updates.dateOfBirth = response.data.dateOfBirth;
              //   }
              //   if (response.data?.gender && !prev.gender) {
              //     updates.gender = response.data.gender;
              //   }
              //   if (response.data?.highestQualification && !prev.highestQualification?.trim()) {
              //     updates.highestQualification = response.data.highestQualification;
              //   }
                
              //   // Merge location details, only update empty fields
              //   if (response.data?.currentLocationDetails) {
              //     updates.currentLocationDetails = {
              //       ...prev.currentLocationDetails,
              //       ...(response.data.currentLocationDetails.street && !prev.currentLocationDetails?.street?.trim() && { street: response.data.currentLocationDetails.street }),
              //       ...(response.data.currentLocationDetails.area && !prev.currentLocationDetails?.area?.trim() && { area: response.data.currentLocationDetails.area }),
              //       ...(response.data.currentLocationDetails.city && !prev.currentLocationDetails?.city?.trim() && { city: response.data.currentLocationDetails.city }),
              //       ...(response.data.currentLocationDetails.pincode && !prev.currentLocationDetails?.pincode?.trim() && { pincode: response.data.currentLocationDetails.pincode }),
              //       ...(response.data.currentLocationDetails.fullAddress && !prev.currentLocationDetails?.fullAddress?.trim() && { fullAddress: response.data.currentLocationDetails.fullAddress }),
              //     };
              //   }
                
              //   if (response.data?.totalExperienceYears !== undefined && prev.totalExperienceYears === 0) {
              //     updates.totalExperienceYears = response.data.totalExperienceYears;
              //   }
              //   if (response.data?.totalExperienceMonths !== undefined && prev.totalExperienceMonths === 0) {
              //     updates.totalExperienceMonths = response.data.totalExperienceMonths;
              //   }
              //   if (response.data?.skills && Array.isArray(response.data.skills) && response.data.skills.length > 0 && prev.skills.length === 0) {
              //     updates.skills = response.data.skills;
              //   }
              //   if (response.data?.spokenLanguages && Array.isArray(response.data.spokenLanguages) && response.data.spokenLanguages.length > 0 && prev.spokenLanguages.length === 0) {
              //     updates.spokenLanguages = response.data.spokenLanguages;
              //   }
              //   if (response.data?.preferredJobCategories && Array.isArray(response.data.preferredJobCategories) && response.data.preferredJobCategories.length > 0 && prev.preferredJobCategories.length === 0) {
              //     updates.preferredJobCategories = response.data.preferredJobCategories;
              //   }
              //   if (response.data?.preferredEmploymentTypes && Array.isArray(response.data.preferredEmploymentTypes) && response.data.preferredEmploymentTypes.length > 0 && prev.preferredEmploymentTypes.length === 0) {
              //     updates.preferredEmploymentTypes = response.data.preferredEmploymentTypes;
              //   }
              //   if (response.data?.preferredWorkTypes && Array.isArray(response.data.preferredWorkTypes) && response.data.preferredWorkTypes.length > 0 && prev.preferredWorkTypes.length === 0) {
              //     updates.preferredWorkTypes = response.data.preferredWorkTypes;
              //   }
                
              //   return {
              //     ...prev,
              //     ...updates
              //   };
              // });
              toast.success("Data extracted from resume!", { id: "resume-extract" });
            }
  
          }
          catch(e){
            setUploadError(true)
            toast.error("Failed to upload resume", { id: "resume-upload" });
            toast.error("Failed to extract data from resume", { id: "resume-extract" });
            console.log(e);
          }
          finally{
            setResloading(false);
            setResumeUploadStep('idle');
          }
        };
        if(resumeFile){
          
          fetchResume()
        }
        
      },[resumeFile,resumeFileName])


  // Fetch jobs on component mount
  useEffect(() => {
    let isCancelled = false;

    const fetchJobs = async () => {
      if (isCancelled) return;
      
      setLoadingJobs(true);
      setJobsError(null);
      try {
        // Use the selection endpoint which bypasses role restrictions
        const response = await getJobsForSelection({
          _t: Date.now()
        });
        
        if (isCancelled) return; // Don't update state if component unmounted
        
        console.log('✅ Jobs for selection fetched successfully:', response);
        
        // Based on the backend response structure: response.data.jobs
        let jobsArray = [];
        if (response && response.data && Array.isArray(response.data.jobs)) {
          jobsArray = response.data.jobs;
          console.log('✅ Using response.data.jobs:', jobsArray);
        } else if (response && Array.isArray(response as any)) {
          jobsArray = response as any;
          console.log('✅ Using direct response as array:', jobsArray);
        } else if (response && Array.isArray(response.data)) {
          jobsArray = response.data;
          console.log('✅ Using response.data:', jobsArray);
        } else if (Array.isArray(response)) {
          jobsArray = response;
          console.log('✅ Using direct response:', jobsArray);
        } else {
          console.warn('⚠️ Unexpected response structure:', response);
          jobsArray = [];
        }
        
        console.log('✅ Final jobs array length:', jobsArray.length);
        console.log('✅ Sample job:', jobsArray[0]);
        setJobs(jobsArray);
      } catch (error) {
        if (isCancelled) return;
        console.error('❌ Error fetching jobs for selection:', error);
        setJobsError('Failed to load jobs. Please try again.');
      } finally {
        if (!isCancelled) {
          setLoadingJobs(false);
        }
      }
    };

    fetchJobs();

    // Cleanup function to prevent state updates if component unmounts
    return () => {
      isCancelled = true;
    };
  }, []);

  // Prefill form when coming from Approaches flow (after jobs are loaded)
  useEffect(() => {
    const payload = approachState?.approachPayload;
    const prefill = approachState?.prefill || {};
    if ((approachState?.fromApproaches || approachState?.fromHotLeads) && payload && !approachedFilled.current && jobs.length > 0) {
      approachedFilled.current = true;
      console.log('Prefilling form with jobId:', payload.jobId, 'Jobs:', jobs.slice(0, 3).map(j => ({ id: j.id || j._id, title: j.title })));
      
      // Find matching job using either payload.jobId or prefill.jobId
      const jobIdToMatch = payload.jobId || prefill.jobId;
      const matchingJob = jobIdToMatch ? jobs.find(job => 
        job.id === jobIdToMatch || job._id === jobIdToMatch || 
        String(job.id) === String(jobIdToMatch) || String(job._id) === String(jobIdToMatch)
      ) : null;
      
      setFormData((prev) => {
        const finalJobId = matchingJob ? (matchingJob.id || matchingJob._id) : (payload.jobId || prefill.jobId || prev.jobId);
        
        return {
          ...prev,
          name: payload.name || prefill.name || prev.name,
          email: payload.email || prefill.email || prev.email,
          phone: payload.phone || prefill.phone || prev.phone,
          highestQualification: payload.qualifications || prefill.highestQualification || prev.highestQualification,
          jobId: finalJobId,
          jobType: prev.jobType || "main",
          dateOfBirth: prefill.dateOfBirth || prev.dateOfBirth,
          skills: Array.isArray(prefill.skills) && prefill.skills.length > 0 ? prefill.skills : prev.skills,
          totalExperienceYears: prefill.workExperience ? Math.trunc(prefill.workExperience) : 0,
          totalExperienceMonths: prefill.workExperience ? Math.round((prefill.workExperience - Math.trunc(prefill.workExperience)) * 12) : 0,
        };
      });
    }
  }, [jobs, approachState]);

  // Effect to scroll to top when step changes
  useEffect(() => {
    // Don't scroll on initial load (step 1)
    if (currentStep > 1) {
      // Try multiple scroll methods for better compatibility
      if (formTopRef.current) {
        formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // Fallback methods
        window.scrollTo({ top: 0, behavior: 'smooth' });
        document.body.scrollTop = 0; // For Safari
        document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
      }
    }
  }, [currentStep]);
  const handleEmailKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleAddEmail()
    }
  }

  const handleRemoveEmail = (emailToRemove: string) => {
    setScheduleFormData((prev) => ({
      ...prev,
      additionalParticipants: prev.additionalParticipants.filter((email) => email !== emailToRemove),
    }))
    toast.success("Removed", {
      duration: 1500,
      style: { whiteSpace: 'nowrap' }
    })
  }


  const handleAddEmail = () => {
    if (emailInput.trim() && isValidEmail(emailInput.trim())) {
      if (!scheduleFormData.additionalParticipants.includes(emailInput.trim())) {
        setScheduleFormData((prev) => ({
          ...prev,
          additionalParticipants: [...prev.additionalParticipants, emailInput.trim()],
        }))
        setEmailInput("")
        toast.success(`Added: ${emailInput.trim()}`, {
          duration: 1500,
          style: { whiteSpace: 'nowrap' }
        })
      } else {
        toast.error("Already added", {
          duration: 1500,
          style: { whiteSpace: 'nowrap' }
        })
      }
    } else {
      toast.error("Invalid email", {
        duration: 1500,
        style: { whiteSpace: 'nowrap' }
      })
    }
  }
  // Filter jobs based on search term
  const filteredJobs = jobs.filter(job => 
    job.title?.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
    job.company?.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
    job.location?.toLowerCase().includes(jobSearchTerm.toLowerCase())
  );

  // Transform jobs for react-dropdown-select
  const jobOptions = useMemo(() => {
    return filteredJobs.map((job, index) => {
      const jobId = job.id || job._id || `job-${index}`;
      const companyName = job.companyName || 'Untitled Company';
      const jobTitle = job.title || 'Untitled Job';
      const location = job.location || '';
      
      return {
        value: jobId,
        label: `${jobTitle} - ${companyName}${location ? ` - ${location}` : ''}`,
        job: job // Keep the full job object for reference
      };
    });
  }, [filteredJobs]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => {
      const newFormData = { ...prev } as any;
      if (field.includes(".")) {
        const [parent, child] = field.split(".");
        newFormData[parent] = { ...newFormData[parent], [child]: value };
      } else {
        newFormData[field] = value;
      }
      return newFormData as CandidateFormData;
    });
    
    // Clear errors for this field when user types
    if (showErrors) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        
        // Clear error for the exact field name
        if (errors[field]) {
          delete newErrors[field];
        }
        
        // Handle nested field errors (like currentLocationDetails.city -> city)
        if (field.includes(".")) {
          const [parent, child] = field.split(".");
          // Clear both the nested field and the simplified field name
          delete newErrors[child];
          delete newErrors[`${parent}.${child}`];
        }
        
        // Special handling for specific field mappings
        if (field === "currentLocationDetails.city") {
          delete newErrors["city"];
        }
        
        return newErrors;
      });
    }
  };

  const commonLanguages = [
    "English",
    "Hindi",
    "Bengali",
    "Telugu",
    "Marathi",
    "Tamil",
    "Gujarati",
    "Kannada",
    "Malayalam",
    "Odia",
    "Punjabi",
    "Assamese",
    "Maithili",
    "Urdu",
    "Nepali",
    "Konkani",
  ];

  const [categoryOptions, setCategoryOptions] = useState<Array<{label: string, value: string}>>([]);

  useEffect(() => {
    const fetchCategoryOptions = async () => {
      try {
        const response = await getJobCategoriesList();
        setCategoryOptions(response.data || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        toast.error('Failed to load categories');
      }
    };
    fetchCategoryOptions();
  }, []);

  const employmentTypeOptions = [
    'Full-time',
    'Part-time', 
    'Contract',
    'Freelance',
    'Internship'
  ];
  const MAX_SELECTABLE_ITEMS = 20;

  type SelectOption = { value: string; label: string };
  const [skillOptions, setSkillOptions] = useState<SelectOption[]>([]);
  const [toolOptions, setToolOptions] = useState<SelectOption[]>([]);
  const [skillsSearchTerm, setSkillsSearchTerm] = useState("");
  const [toolsSearchTerm, setToolsSearchTerm] = useState("");

  const selectStyles: StylesConfig<SelectOption, true> = {
    control: (base, state) => ({
      ...base,
      minHeight: 56,
      borderRadius: 12,
      borderWidth: 2,
      borderColor: state.isFocused ? "#f97316" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(249, 115, 22, 0.15)" : "none",
      "&:hover": { borderColor: "#fb923c" },
    }),
    placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: "14px" }),
    valueContainer: (base) => ({ ...base, padding: "6px 12px" }),
    menu: (base) => ({ ...base, zIndex: 50 }),
  };

  const handleMultiSelectChange = (
    fieldName: "skills" | "knownTools",
    selectedOptions: MultiValue<SelectOption>,
  ) => {
    if (selectedOptions.length > MAX_SELECTABLE_ITEMS) {
      toast.error(`You can add up to ${MAX_SELECTABLE_ITEMS} items only`);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [fieldName]: selectedOptions.map((option) => option.value),
    }));
  };

  useEffect(() => {
    let isMounted = true;
    const timer = setTimeout(async () => {
      try {
        const response = await getSkillsList({ search: skillsSearchTerm, limit: 20 });
        if (!isMounted) return;
        const options = (response?.data || []).map((item: string) => ({ value: item, label: item }));
        setSkillOptions(options);
      } catch (error) {
        if (isMounted) {
          setSkillOptions([]);
        }
      }
    }, 250);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [skillsSearchTerm]);

  useEffect(() => {
    let isMounted = true;
    const timer = setTimeout(async () => {
      try {
        const response = await getToolsList({ search: toolsSearchTerm, limit: 20 });
        if (!isMounted) return;
        const options = (response?.data || []).map((item: string) => ({ value: item, label: item }));
        setToolOptions(options);
      } catch (error) {
        if (isMounted) {
          setToolOptions([]);
        }
      }
    }, 250);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [toolsSearchTerm]);

  const removeFromArray = (fieldName: keyof CandidateFormData, index: number) => {
    setFormData((prev) => {
      const newArray = [...(prev[fieldName] as string[])];
      newArray.splice(index, 1);
      return {
        ...prev,
        [fieldName]: newArray,
      };
    });
  };

  // Helper functions for searchable dropdowns
  const handleCategorySelect = (categoryValue: string) => {
    if (!formData.preferredJobCategories.includes(categoryValue)) {
      setFormData((prev) => ({
        ...prev,
        preferredJobCategories: [...prev.preferredJobCategories, categoryValue]
      }));
      
      // Clear error when a category is added
      if (showErrors && errors.preferredJobCategories) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.preferredJobCategories;
          return newErrors;
        });
      }
    }
    setOpenCategoryDropdown(false);
  };

  // Helper to get category label from value
  const getCategoryLabel = (value: string) => {
    const category = categoryOptions.find(cat => cat.value === value);
    return category?.label || value;
  };

  const handleEmploymentTypeSelect = (type: string) => {
    if (!formData.preferredEmploymentTypes.includes(type)) {
      setFormData((prev) => ({
        ...prev,
        preferredEmploymentTypes: [...prev.preferredEmploymentTypes, type]
      }));
    } else {
      // Remove if already selected
      setFormData((prev) => ({
        ...prev,
        preferredEmploymentTypes: prev.preferredEmploymentTypes.filter(t => t !== type)
      }));
    }
    
    // Clear error when first employment type is selected
    if (showErrors && errors.preferredEmploymentTypes && formData.preferredEmploymentTypes.length === 0) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.preferredEmploymentTypes;
        return newErrors;
      });
    }
  };


  const handleResumeUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setResumeFileName(file.name);
      setResumeFile(file);
    }
  };

  const clearResume = () => {
    setResumeFileName(null);
    setResumeFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validateAadhar = (aadhar: string): boolean => {
    const aadharRegex = /^\d{12}$/;
    return aadharRegex.test(aadhar.replace(/\s/g, ''));
  };

  const validatePhone = (phone: string): boolean => {
    // Remove spaces and hyphens for validation
    const cleanPhone = phone.replace(/[\s-]/g, '');
    
    // Allow phone numbers with or without +91/91 prefix
    // Should be 10 digits (without prefix) or 12/13 digits (with prefix)
    const phoneRegex = /^(\+91|91)?[0-9]{10}$/;
    
    // Additional check: if it's a 10-digit number, it should not start with 0 or 1
    if (cleanPhone.length === 10) {
      return /^[1-9]\d{9}$/.test(cleanPhone);
    }
    
    // If it has country code, extract the 10-digit part and validate
    if (cleanPhone.startsWith('+91') && cleanPhone.length === 13) {
      const phoneNumber = cleanPhone.substring(3);
      return /^[2-9]\d{9}$/.test(phoneNumber);
    }
    
    if (cleanPhone.startsWith('91') && cleanPhone.length === 12) {
      const phoneNumber = cleanPhone.substring(2);
      return /^[2-9]\d{9}$/.test(phoneNumber);
    }
    
    return phoneRegex.test(cleanPhone);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to check if fields should be mandatory based on company
  const shouldFieldsBeMandatory = (): boolean => {
    if (!formData.jobId) return false;
   
    
    const specialCompanies = process.env.REACT_APP_COMPANY_REQUIRE_MANDATORY_FIELDS?.split(",") || [];
    console.log("specialCompanies",process.env.REACT_APP_COMPANY_REQUIRE_MANDATORY_FIELDS)
    // Find the selected job from the jobs array
    const selectedJob = jobs.find(job => job.id === formData.jobId || job._id === formData.jobId);

    console.log("selectedJob.companyName",selectedJob?.companyName || "No company name");
    
    if (!selectedJob || !selectedJob.companyName) return false;
    
    return specialCompanies?.includes(selectedJob.companyName);
  };

  const validateStep1 = (): { isValid: boolean; errors: Record<string, string> } => {
    const newErrors: Record<string, string> = {};
    const isMandatory = shouldFieldsBeMandatory();

    if (!formData.name.trim()) newErrors.name = "Full name is required";
    if (!formData.fatherName.trim()) newErrors.fatherName = "Father's name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = "Please enter a valid 10-digit phone number";
    }
    if (!formData.jobId.trim()) newErrors.jobId = "Job selection is required";
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = "Date of birth is required";
    } else {
      // Check if candidate is at least 16 years old
      const today = new Date();
      const birthDate = new Date(formData.dateOfBirth);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      // Calculate exact age considering month and day
      const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) 
        ? age - 1 
        : age;
      
      if (actualAge < 16) {
        newErrors.dateOfBirth = "Candidate must be at least 16 years old";
      }
    }

    // Conditional validation for new fields (mandatory for special companies)
    if (isMandatory) {
      if (!formData.alternativePhoneNumber || !formData.alternativePhoneNumber.trim()) {
        newErrors.alternativePhoneNumber = "Alternative phone number is required for this job position";
      } else if (!validatePhone(formData.alternativePhoneNumber)) {
        newErrors.alternativePhoneNumber = "Please enter a valid 10-digit alternative phone number";
      }
      if (!formData.maritalStatus || !formData.maritalStatus.trim()) {
        newErrors.maritalStatus = "Marital status is required for this job position";
      }
      if (!formData.howOftenAbleToJoin || !formData.howOftenAbleToJoin.trim()) {
        newErrors.howOftenAbleToJoin = "How often you are able to join is required for this job position";
      }
      
      // Make aadharNumber mandatory
      if (!formData.aadharNumber || !formData.aadharNumber.trim()) {
        newErrors.aadharNumber = "Aadhar number is required for this job position";
      } else if (!validateAadhar(formData.aadharNumber)) {
        newErrors.aadharNumber = "Please enter a valid 12-digit Aadhar number";
      }
      
      // Make resume mandatory - check if file is uploaded OR if we have the uploaded URL
      if (!resumeFile && !uploadedURL) {
        newErrors.resume = "Resume upload is required for this job position";
      }
      
      // Make all address fields mandatory
      if (!formData.currentLocationDetails.street || !formData.currentLocationDetails.street.trim()) {
        newErrors.street = "Street address is required for this job position";
      }
      if (!formData.currentLocationDetails.area || !formData.currentLocationDetails.area.trim()) {
        newErrors.area = "Area/Locality is required for this job position";
      }
      if (!formData.currentLocationDetails.pincode || !formData.currentLocationDetails.pincode.trim()) {
        newErrors.pincode = "Pincode is required for this job position";
      }
    }

    if (!formData.currentLocationDetails.city.trim()) newErrors.city = "City is required";
    if (formData.spokenLanguages.length === 0) newErrors.spokenLanguages = "At least one language is required";

    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const validateStep2 = (): { isValid: boolean; errors: Record<string, string> } => {
    const newErrors: Record<string, string> = {};

    if (!formData.highestQualification.trim()) newErrors.highestQualification = "Educational qualification is required";
    
    // Validate work experience - both years and months are required
    if (formData.totalExperienceYears === undefined  || formData.totalExperienceYears === null || isNaN(formData.totalExperienceYears) ) {
      newErrors.totalExperienceYears = "Work experience (years) is required";
    }
    
    // Validate months - now required
    if (formData.totalExperienceMonths === undefined || formData.totalExperienceYears === null || isNaN(formData.totalExperienceYears) || formData.totalExperienceMonths > 11) {
      newErrors.totalExperienceMonths = "Additional months is required and should be between 0-11";
    }
    
    if (formData.preferredEmploymentTypes.length === 0) newErrors.preferredEmploymentTypes = "At least one employment type is required";
    if (formData.preferredWorkTypes.length === 0) newErrors.preferredWorkTypes = "At least one work type is required";

    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      
      // Only validate and submit when we're on the final step (step 2)
    // Validate Step 1 before proceeding
    const validation = validateStep1();
    if (!validation.isValid) {
      setErrors(validation.errors);
      setShowErrors(true);
      // Scroll to first error field
      const firstErrorField = Object.keys(validation.errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return; // Don't proceed if validation fails
    }

   

      const validation1 = validateStep2();
      if (!validation1.isValid) {
        setErrors(validation1.errors);
        setShowErrors(true);
        const firstErrorField = Object.keys(validation1.errors)[0];
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
        return;
      }

      // Show the interview scheduling modal
      setShowInterviewModal(true);
    },
    [validateStep1, validateStep2],
  );

  // Actual submission handler after modal confirmation
  const handleActualSubmit = useCallback(
    async () => {
      // Start loading state
      setIsSubmitting(true);

      // Fetch all applications and check for duplicate phone number before submitting
      try {
        

        // Create normalized data without any status field - let backend handle initial status
        const normalizedData: ICreateApplicationFormData = {
          jobId: formData.jobId,
          jobType: formData.jobType,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          fatherName: formData.fatherName,
          dateOfBirth: formData.dateOfBirth,
          gender: formData.gender,
          ...(formData.aadharNumber && formData.aadharNumber.trim() && { aadharNumber: formData.aadharNumber }),
          ...(formData.alternativePhoneNumber && formData.alternativePhoneNumber.trim() && { alternativePhoneNumber: formData.alternativePhoneNumber }),
          ...(formData.maritalStatus && formData.maritalStatus.trim() && { maritalStatus: formData.maritalStatus }),
          ...(formData.howOftenAbleToJoin && formData.howOftenAbleToJoin.trim() && { howOftenAbleToJoin: formData.howOftenAbleToJoin }),
          highestQualification: formData.highestQualification,
          currentLocationDetails: formData.currentLocationDetails,
          totalExperienceYears: formData.totalExperienceYears,
          totalExperienceMonths: formData.totalExperienceMonths,
          skills: formData.skills.length > 0 ? formData.skills : ["General"],
          knownTools: formData.knownTools.length > 0 ? formData.knownTools : ["General"],
          spokenLanguages: formData.spokenLanguages.length > 0 ? formData.spokenLanguages : ["English"],
          preferredJobCategories: formData.preferredJobCategories.length > 0 ? formData.preferredJobCategories : ["General"],
          preferredEmploymentTypes: formData.preferredEmploymentTypes.length > 0 ? formData.preferredEmploymentTypes : ["Full-time"],
          preferredWorkTypes: formData.preferredWorkTypes.length > 0 ? formData.preferredWorkTypes : ["on-site"],
          scheduleInterview: scheduleInterview,
          ...(approachState?.fromHotLeads
            ? { sourceMechanism: "hot_leads" as const }
            : approachState?.fromApproaches
              ? { sourceMechanism: "approaches" as const }
              : {}),
        };

        // Add resumeUrl if available
        if (uploadedURL && uploadedURL.trim()) {
          normalizedData.resumeUrl = uploadedURL;
          console.log("✅ Resume URL added to payload:", uploadedURL);
        } else {
          console.warn("⚠️ No resume URL available. uploadedURL:", uploadedURL);
        }

        console.log("Uploaded URL state:", uploadedURL);
        console.log("Submitting candidate data:", normalizedData);
        console.log("Schedule 1st round interview:", scheduleInterview);

        // Call the API without the resume file for now
        const applicationResponse = await apiClient(normalizedData);
        
        // TODO: If scheduleInterview is true, call interview scheduling API here
        if (scheduleInterview) {
          // Add your interview scheduling logic here
          console.log("Application Response:", applicationResponse?.data);
          console.log("Schedule Form Data:", scheduleFormData);
          
          // Validate required fields
          if (!scheduleFormData.scheduledDate || !scheduleFormData.startTime || !scheduleFormData.endTime) {
            toast.error("Please fill in all required interview fields (Date, Start Time, End Time)");
            setIsSubmitting(false);
            return;
          }
          
          if (scheduleFormData.interviewType === "Offline" && !scheduleFormData.location) {
            toast.error("Please provide a location for offline interview");
            setIsSubmitting(false);
            return;
          }
          
          const interviewId = applicationResponse?.data?.interviewId;
          const roundId = applicationResponse?.data?.roundId;
          console.log("Scheduling interview for ID:", interviewId, "Round ID:", roundId);
          
          // Add current user to interviewers if not already added
          const finalScheduleData = {
            ...scheduleFormData,
            interviewers: user?.id ? [user.id, ...scheduleFormData.interviewers] : scheduleFormData.interviewers
          };
          
          console.log("Final Schedule Data:", finalScheduleData);
          await scheduleInterviewRound(interviewId, roundId, finalScheduleData);
          toast.success("Interview scheduled successfully!");
        }
        
        // Hot Leads: existing approach — mark Schedule after candidate is added (removes hot lead via sync)
        if (
          approachState?.existingApproachId &&
          approachState?.approachPayload?.callStatus === "Schedule"
        ) {
          await updateApproach(approachState.existingApproachId, {
            callStatus: "Schedule",
            applicationId: applicationResponse?.data?.applicationId,
          });
          navigate(window.location.pathname, { replace: true, state: {} });
        } else if (approachState?.fromApproaches && approachState?.approachPayload?.callStatus === "Schedule") {
          await createApproach({
            ...approachState.approachPayload,
            applicationId: applicationResponse?.data?.applicationId,
          });
          navigate(window.location.pathname, { replace: true, state: {} });
        }

        setSuccessMessage(
          `New candidate profile created for ${formData.email} by ${performingUser.email}${scheduleInterview ? ' - Interview will be scheduled' : ''}`
        );

        if (onSubmit) {
          onSubmit(formData);
        }

        if (refreshCandidates) {
          refreshCandidates();
        }

        // Show loading for navigation with better UX messaging
        setTimeout(() => {
          setIsSubmitting(false);
          navigate("/view-applications");
        }, 2000);
      } catch (error: any) {
        setIsSubmitting(false); // Stop loading on error
        console.error("Error creating application:", error.message);
        if (isBlockedPhoneError(error)) {
          setBlockedPopupMessage(getApproachApiErrorMessage(error));
          setBlockedPopupOpen(true);
          return;
        }
        // Add better error handling
        if (error.response?.data?.message) {
          // Show toast for duplicate email or phone
         
              toast.error(error.response?.data?.message);
         
          console.error("Backend error:", error.response.data.message);
        }
      }
    },
    [formData, onSubmit, refreshCandidates, navigate, apiClient, performingUser.email, scheduleInterview, scheduleFormData, user, uploadedURL, approachState],
  );

  const getInputClassName = (fieldName: string, baseClassName: string = "") => {
    const hasError = showErrors && errors[fieldName];
    const errorClasses = hasError ? "border-red-500 focus:border-red-500 focus:ring-red-500/20 bg-red-50" : "";
    return `${baseClassName} ${errorClasses}`.trim();
  };
  const handlePreviewResume = () => {
    if (uploadedURL) {
      try {
        window.open(uploadedURL, '_blank');
      } catch (e) {
        console.error("Error opening resume:", e);
        toast.error("Failed to preview resume. Please try again.");
      }
    } else {
      toast.error("Resume URL not available. Please wait for the upload to complete.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
      <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 py-4">
        {/* Enhanced Header Section */}
        <div ref={formTopRef} className="mb-8">
          <div className="mb-4 sm:mb-6">
            <div className="flex flex-col">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900">
                Add New Candidate
              </h1>
              <p className="text-gray-600 text-sm sm:text-lg font-medium">
                Create a comprehensive candidate profile with detailed information
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 lg:space-y-8">
          {/* Personal Information Card */}
          <Card className="shadow-xl rounded-2xl border-0 bg-white/90 backdrop-blur-sm overflow-hidden">
            <CardContent className="p-4 sm:p-6 lg:p-8 py-4 sm:py-6 lg:py-8">
              {/* Section Header */}
             
              {/* Basic Information */}
              <div className="space-y-4 sm:space-y-6 lg:space-y-8">
                  {/* Resume Upload Section */}
                <div className="flex flex-col lg:flex-row w-full justify-between gap-4 lg:gap-0">
                  <div className="flex items-center gap-4 pb-4 mb-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                      <FileText className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-md font-bold text-gray-900">
                        Resume Upload {shouldFieldsBeMandatory() && <span className="text-red-500">*</span>}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Upload candidate's resume {shouldFieldsBeMandatory() ? "(required)" : "(optional)"}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <Input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleResumeUpload}
                        className="hidden"
                        accept=".pdf,.doc,.docx"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={resloading}
                        className={`flex items-center gap-3 h-12 sm:h-14 px-6 rounded-xl border-2 border-dashed transition-all duration-200 w-full sm:w-auto font-medium ${
                          showErrors && errors.resume 
                            ? 'border-red-300 hover:border-red-500 hover:bg-red-50 text-red-700' 
                            : 'border-orange-300 hover:border-orange-500 hover:bg-orange-50 text-orange-700'
                        } ${resloading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {resloading ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            <span className="truncate">
                              {resumeUploadStep === 'uploading' ? 'Uploading...' : resumeUploadStep === 'extracting' ? 'Extracting data...' : 'Processing...'}
                            </span>
                          </>
                        ) : (
                          <>
                            <UploadCloud className="h-5 w-5" />
                            <span className="truncate">
                              {resumeFileName ? resumeFileName : shouldFieldsBeMandatory() ? "Choose Resume File (Required)" : "Choose Resume File (Optional)"}
                            </span>
                          </>
                        )}
                      </Button>
                      {showErrors && errors.resume && (
                        <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                          <X className="h-4 w-4" />
                          {errors.resume}
                        </p>
                      )}
                      {resumeFileName && (
                      <>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={clearResume}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl"
                          disabled={resloading}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handlePreviewResume}
                          className="flex items-center gap-2 text-orange-600 border-orange-300 hover:bg-orange-50 rounded-xl font-medium"
                          disabled={resloading || !uploadedURL}
                        >
                          <Eye className="h-4 w-4" />
                          Preview
                        </Button>
                      </>
                    )}
                    </div>
                    {resumeFileName && !resloading && uploadedURL && !UploadError && (
                      <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-xl">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="text-sm font-medium text-green-800">{resumeFileName} uploaded successfully</span>
                      </div>
                    )}
                    {UploadError && (
                      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                        <span className="text-sm font-medium text-red-800">Failed to upload resume. Please try again.</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-4 sm:space-y-6 lg:space-y-8">
                  
                  <div className="space-y-3">
                    <Label htmlFor="jobId" className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <Target className="h-4 w-4 text-orange-600" />
                      Job Position <span className="text-red-500">*</span>
                    </Label>
                    {loadingJobs ? (
                      <div className={getInputClassName("jobId", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 bg-white font-medium shadow-sm flex items-center justify-center")}>
                        <div className="flex items-center gap-3">
                          <Loader2 className="h-5 w-5 animate-spin text-orange-600" />
                          <span className="text-gray-600">Loading available positions...</span>
                        </div>
                      </div>
                    ) : jobsError ? (
                      <div className={getInputClassName("jobId", "h-12 sm:h-14 rounded-xl border-2 border-red-200 bg-red-50 font-medium shadow-sm flex items-center justify-center")}>
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-red-600 font-medium text-sm">{jobsError}</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.location.reload()}
                            className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                          >
                            Retry Loading
                          </Button>
                        </div>
                      </div>
                    ) : !Array.isArray(jobs) || jobs.length === 0 ? (
                      <div className={getInputClassName("jobId", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 bg-white font-medium shadow-sm flex items-center justify-center")}>
                        <div className="flex flex-col items-center gap-2">
                          <Briefcase className="h-6 w-6 text-gray-300" />
                          <span className="text-gray-500 text-sm font-medium">
                            {!Array.isArray(jobs) ? 'Invalid job data format' : 'No positions available'}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full">
                        <Dropdown
                          options={jobOptions}
                          values={jobOptions.filter(option => option.value === formData.jobId)}
                          onChange={(selectedOptions) => {
                            if (selectedOptions && selectedOptions.length > 0) {
                              const selectedOption = selectedOptions[0];
                              handleInputChange("jobId", selectedOption.value);
                              const selectedJob = selectedOption.job;
                              if (selectedJob && selectedJob.location && selectedJob.location.trim() !== "") {
                                setScheduleFormData((prev) => ({ ...prev, location: selectedJob.location }));
                              }
                            } else {
                              handleInputChange("jobId", "");
                            }
                          }}
                          searchable={false}
                          placeholder={jobOptions.length === 0 ? "No positions available" : "Search and select a job position"}
                          noDataLabel="No jobs match your search"
                          disabled={loadingJobs}
                          className={getInputClassName("jobId", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium shadow-sm hover:shadow-md transition-all duration-200")}
                          style={{
                            width: '100%',
                            minHeight: '48px',
                            fontSize: '14px'
                          }}
                          contentRenderer={({ props, state, methods }) => {
                            const selectedOption = props.values && props.values.length > 0 ? props.values[0] : null;
                            return (
                              <div className="flex items-center justify-between w-full px-3 py-2">
                                <span className={selectedOption ? "text-gray-900" : "text-gray-500"}>
                                  {selectedOption ? selectedOption.label : props.placeholder}
                                </span>
                                <ChevronDown className="h-4 w-4 text-gray-400" />
                              </div>
                            );
                          }}
                          itemRenderer={({ item, itemIndex, props, state, methods }) => (
                            <div
                              key={itemIndex}
                              onClick={() => methods.addItem(item)}
                              className="px-4 py-3 hover:bg-orange-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              <div className="flex flex-col gap-1">
                                <span className="font-medium text-gray-900 text-sm">{item.job?.title || 'Untitled Job'}</span>
                                <span className="text-xs text-gray-600">
                                  {`${item.job?.companyName || 'Untitled Company'}${item.job?.location ? ` - ${item.job.location}` : ''}`}
                                </span>
                              </div>
                            </div>
                          )}
                          dropdownRenderer={({ props, state, methods }) => {
                            // Use jobSearchTerm for filtering instead of state.search
                            const regexp = new RegExp(jobSearchTerm, 'i');
                            const filteredOptions = props.options.filter((option: any) => 
                              regexp.test(option.label) || regexp.test(option.job?.title || '') || 
                              regexp.test(option.job?.companyName || '') || regexp.test(option.job?.location || '')
                            );
                            
                            // Get the trigger width from selectBounds to match dropdown width
                            const triggerWidth = state.selectBounds && typeof state.selectBounds === 'object' && 'width' in state.selectBounds 
                              ? (state.selectBounds as DOMRect).width 
                              : '100%';
                            
                            return (
                              <div 
                                className="rounded-xl border-2 border-gray-200 shadow-xl bg-white max-h-80 overflow-y-auto"
                                style={{ width: triggerWidth }}
                              >
                                {/* Search Input */}
                                <div className="p-4 border-b border-gray-200 bg-gray-50">
                                  <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <Input
                                      placeholder="Search jobs by title, company, or location..."
                                      value={jobSearchTerm}
                                      onChange={(e) => setJobSearchTerm(e.target.value)}
                                      onKeyDown={(e) => e.stopPropagation()} 
                                      className="pl-10 h-12 rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20"
                                    />
                                  </div>
                                </div>
                                
                                {filteredOptions.length === 0 ? (
                                  <div className="p-8 text-center text-gray-500">
                                    <div className="flex flex-col items-center gap-3">
                                      <Search className="h-12 w-12 text-gray-300" />
                                      <span className="font-medium text-lg">No jobs match your search</span>
                                      <span className="text-sm">Try different keywords</span>
                                    </div>
                                  </div>
                                ) : (
                                  filteredOptions.map((option: any, index: number) => (
                                    <div
                                      key={index}
                                      onClick={() => methods.addItem(option)}
                                      className="px-4 py-3 hover:bg-orange-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                    >
                                      <div className="flex flex-col gap-1">
                                        <span className="font-medium text-gray-900 text-sm">{option.job?.title || 'Untitled Job'}</span>
                                        <span className="text-xs text-gray-600">
                                          {`${option.job?.companyName || 'Untitled Company'}${option.job?.location ? ` - ${option.job.location}` : ''}`}
                                        </span>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            );
                          }}
                        />
                      </div>
                    )}
                    {showErrors && errors.jobId && (
                      <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                        <X className="h-4 w-4" />
                        {errors.jobId}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
                  <div className="space-y-3">
                    <Label htmlFor="name" className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <User className="h-4 w-4 text-orange-600" />
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      placeholder="Enter candidate's full name"
                      className={getInputClassName("name", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium text-gray-900 placeholder:text-gray-400 transition-all duration-200")}
                      required
                    />
                    {showErrors && errors.name && (
                      <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                        <X className="h-4 w-4" />
                        {errors.name}
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <Label htmlFor="fatherName" className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <User className="h-4 w-4 text-orange-600" />
                      Father's Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="fatherName"
                      value={formData.fatherName}
                      onChange={(e) => handleInputChange("fatherName", e.target.value)}
                      placeholder="Enter father's name"
                      className={getInputClassName("fatherName", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium text-gray-900 placeholder:text-gray-400 transition-all duration-200")}
                    />
                    {showErrors && errors.fatherName && (
                      <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                        <X className="h-4 w-4" />
                        {errors.fatherName}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-3">
                    <Label htmlFor="email" className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <Mail className="h-4 w-4 text-orange-600" />
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      placeholder="candidate@example.com"
                      className={getInputClassName("email", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium text-gray-900 placeholder:text-gray-400 transition-all duration-200")}
                      required
                    />
                    {showErrors && errors.email && (
                      <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                        <X className="h-4 w-4" />
                        {errors.email}
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <Label htmlFor="phone" className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <Phone className="h-4 w-4 text-orange-600" />
                      Phone Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      maxLength={10}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      placeholder="9876543210"
                      className={getInputClassName("phone", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium text-gray-900 placeholder:text-gray-400 transition-all duration-200")}
                      required
                    />
                    {showErrors && errors.phone && (
                      <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                        <X className="h-4 w-4" />
                        {errors.phone}
                      </p>
                    )}
                  </div>
                </div>

                {/* Additional Fields Section - Mandatory based on company */}
                {(() => {
                  const isMandatory = shouldFieldsBeMandatory();
                  
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div className="space-y-3">
                        <Label htmlFor="alternativePhoneNumber" className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                          <Phone className="h-4 w-4 text-orange-600" />
                          Alternative Phone Number {isMandatory && <span className="text-red-500">*</span>}
                        </Label>
                        <Input
                          id="alternativePhoneNumber"
                          value={formData.alternativePhoneNumber || ""}
                          maxLength={10}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                            handleInputChange("alternativePhoneNumber", value);
                          }}
                          placeholder="9876543210"
                          className={getInputClassName("alternativePhoneNumber", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium text-gray-900 placeholder:text-gray-400 transition-all duration-200")}
                        />
                        {showErrors && errors.alternativePhoneNumber && (
                          <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                            <X className="h-4 w-4" />
                            {errors.alternativePhoneNumber}
                          </p>
                        )}
                      </div>
                      
                      <div className="space-y-3">
                        <Label htmlFor="maritalStatus" className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                          <User className="h-4 w-4 text-orange-600" />
                          Marital Status {isMandatory && <span className="text-red-500">*</span>}
                        </Label>
                        <Select
                          value={formData.maritalStatus || ""}
                          onValueChange={(value) => handleInputChange("maritalStatus", value)}
                        >
                          <SelectTrigger className={getInputClassName("maritalStatus", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium")}>
                            <SelectValue placeholder="Select marital status" />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl">
                            <SelectItem value="Single">Single</SelectItem>
                            <SelectItem value="Married">Married</SelectItem>
                            <SelectItem value="Divorced">Divorced</SelectItem>
                            <SelectItem value="Widowed">Widowed</SelectItem>
                          </SelectContent>
                        </Select>
                        {showErrors && errors.maritalStatus && (
                          <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                            <X className="h-4 w-4" />
                            {errors.maritalStatus}
                          </p>
                        )}
                      </div>
                      
                      <div className="space-y-3">
                        <Label htmlFor="howOftenAbleToJoin" className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-orange-600" />
                          How Often Will You Be Able to Join {isMandatory && <span className="text-red-500">*</span>}
                        </Label>
                        <Select
                          value={formData.howOftenAbleToJoin || ""}
                          onValueChange={(value) => handleInputChange("howOftenAbleToJoin", value)}
                        >
                          <SelectTrigger className={getInputClassName("howOftenAbleToJoin", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium")}>
                            <SelectValue placeholder="Select availability" />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl">
                            <SelectItem value="Immediately">Immediately</SelectItem>
                            <SelectItem value="Within 1 week">Within 1 week</SelectItem>
                            <SelectItem value="Within 2 weeks">Within 2 weeks</SelectItem>
                            <SelectItem value="Within 1 month">Within 1 month</SelectItem>
                            <SelectItem value="Within 2 months">Within 2 months</SelectItem>
                            <SelectItem value="More than 2 months">More than 2 months</SelectItem>
                          </SelectContent>
                        </Select>
                        {showErrors && errors.howOftenAbleToJoin && (
                          <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                            <X className="h-4 w-4" />
                            {errors.howOftenAbleToJoin}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Job Information Section */}
               

                {/* Personal Details Section */}
                <div className="space-y-4 sm:space-y-6 lg:space-y-8">
                  

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                    <div className="space-y-3">
                      <Label htmlFor="dateOfBirth" className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-orange-600" />
                        Date of Birth <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="dateOfBirth"
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
                        className={getInputClassName("dateOfBirth", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium transition-all duration-200")}
                        max="2009-08-20"
                        defaultValue="2009-08-20"
                      />
                      {showErrors && errors.dateOfBirth && (
                        <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                          <X className="h-4 w-4" />
                          {errors.dateOfBirth}
                        </p>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      <Label htmlFor="gender" className="text-sm font-semibold text-gray-800">Gender <span className="text-red-500">*</span></Label>
                      <Select
                        value={formData.gender}
                        onValueChange={(value) =>
                          handleInputChange("gender", value as "Male" | "Female" | "Other")
                        }
                      >
                        <SelectTrigger className="h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Female">Female</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-3">
                      <Label htmlFor="aadharNumber" className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        <FileText className="h-4 w-4 text-orange-600" />
                        Aadhar Number {shouldFieldsBeMandatory() ? <span className="text-red-500">*</span> : "(optional)"}
                      </Label>
                      <Input
                        id="aadharNumber"
                        value={formData.aadharNumber}
                        onChange={(e) => handleInputChange("aadharNumber", e.target.value)}
                        placeholder="Enter Aadhar number"
                        className={getInputClassName("aadharNumber", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium placeholder:text-gray-400 transition-all duration-200")}
                      />
                      {showErrors && errors.aadharNumber && (
                        <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                          <X className="h-4 w-4" />
                          {errors.aadharNumber}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

              

                {/* Location Details Section */}
                <div className="space-y-4 sm:space-y-6 lg:space-y-8">
                  

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-3">
                      <Label htmlFor="street" className="text-sm font-semibold text-gray-800">
                        Street Address {shouldFieldsBeMandatory() && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="street"
                        value={formData.currentLocationDetails.street}
                        onChange={(e) => handleInputChange("currentLocationDetails.street", e.target.value)}
                        placeholder="Enter street address"
                        className={getInputClassName("street", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium placeholder:text-gray-400 transition-all duration-200")}
                      />
                      {showErrors && errors.street && (
                        <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                          <X className="h-4 w-4" />
                          {errors.street}
                        </p>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      <Label htmlFor="area" className="text-sm font-semibold text-gray-800">
                        Area/Locality {shouldFieldsBeMandatory() && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="area"
                        value={formData.currentLocationDetails.area}
                        onChange={(e) => handleInputChange("currentLocationDetails.area", e.target.value)}
                        placeholder="Enter area or locality"
                        className={getInputClassName("area", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium placeholder:text-gray-400 transition-all duration-200")}
                      />
                      {showErrors && errors.area && (
                        <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                          <X className="h-4 w-4" />
                          {errors.area}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-6">
                    <div className="space-y-3">
                      <Label htmlFor="city" className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-orange-600" />
                        City <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="city"
                        value={formData.currentLocationDetails.city}
                        onChange={(e) => handleInputChange("currentLocationDetails.city", e.target.value)}
                        placeholder="Enter city name"
                        className={getInputClassName("city", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium placeholder:text-gray-400 transition-all duration-200")}
                        required
                      />
                      {showErrors && errors.city && (
                        <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                          <X className="h-4 w-4" />
                          {errors.city}
                        </p>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      <Label htmlFor="pincode" className="text-sm font-semibold text-gray-800">
                        Pincode {shouldFieldsBeMandatory() && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="pincode"
                        value={formData.currentLocationDetails.pincode}
                        onChange={(e) => handleInputChange("currentLocationDetails.pincode", e.target.value)}
                        placeholder="Enter pincode"
                        className={getInputClassName("pincode", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium placeholder:text-gray-400 transition-all duration-200")}
                      />
                      {showErrors && errors.pincode && (
                        <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                          <X className="h-4 w-4" />
                          {errors.pincode}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Languages Section */}
                <div className="space-y-4 sm:space-y-6 lg:space-y-8">
                 

                  <div className="space-y-4">
                    <Label className="text-sm font-semibold text-gray-800">Select Languages <span className="text-red-500">*</span></Label>
                    <Select 
                      value={selectedLanguage}
                      onValueChange={(value) => {
                        if (value && !formData.spokenLanguages.includes(value)) {
                          setFormData((prev) => ({
                            ...prev,
                            spokenLanguages: [...prev.spokenLanguages, value],
                          }));
                          
                          setSelectedLanguage("");
                          
                          if (showErrors && errors.spokenLanguages) {
                            setErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.spokenLanguages;
                              return newErrors;
                            });
                          }
                        }
                      }}>
                      <SelectTrigger className={getInputClassName("spokenLanguages", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium")}>
                        <SelectValue placeholder="Select a language to add" />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        {commonLanguages
                          .filter(lang => !formData.spokenLanguages.includes(lang))
                          .map((lang) => (
                            <SelectItem key={lang} value={lang} className="cursor-pointer">
                              {lang}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {showErrors && errors.spokenLanguages && (
                      <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                        <X className="h-4 w-4" />
                        {errors.spokenLanguages}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-3 mt-4">
                      {formData.spokenLanguages.map((language, index) => (
                        <Badge key={index} variant="secondary" className="gap-2 py-3 px-4 bg-orange-50 text-orange-700 border-2 border-orange-200 rounded-xl font-medium hover:bg-orange-100 transition-colors">
                          {language}
                          <X
                            className="h-4 w-4 cursor-pointer hover:text-red-500 transition-colors"
                            onClick={() => removeFromArray("spokenLanguages", index)}
                          />
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{marginTop:"20px"}}>
                

                  <div className="space-y-6">
                    <div className="space-y-3">
                      <Label htmlFor="highestQualification" className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        <Award className="h-4 w-4 text-orange-600" />
                        Educational Qualification <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.highestQualification}
                        onValueChange={(value) => handleInputChange("highestQualification", value)}
                      >
                        <SelectTrigger className={getInputClassName("highestQualification", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium")}>
                          <SelectValue placeholder="Select your highest qualification" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl max-h-60">
                          {qualificationOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value} className="cursor-pointer">
                              <div className="flex flex-col py-1">
                                <span className="font-medium">{option.label}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {showErrors && errors.highestQualification && (
                        <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                          <X className="h-4 w-4" />
                          {errors.highestQualification}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div className="space-y-3">
                        <Label htmlFor="experienceYears" className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                          <Clock className="h-4 w-4 text-orange-600" />
                          Work Experience (Years) <span className="text-red-500">*</span>
                        </Label>
                        <Input
                       id="experienceYears"
                          type="number"
                          min="0"
                          value={formData.totalExperienceYears === 0 ? "0" : formData.totalExperienceYears || ""}
                        onChange={(e) => handleInputChange("totalExperienceYears", e.target.value === "" ? "" : Number(e.target.value))}
                          className={getInputClassName("totalExperienceYears", "h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}
                          required
                        />
                        {showErrors && errors.totalExperienceYears && (
                          <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                            <X className="h-4 w-4" />
                            {errors.totalExperienceYears}
                          </p>
                        )}
                      </div>
                      
                      <div className="space-y-3">
                        <Label htmlFor="experienceMonths" className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                          <Clock className="h-4 w-4 text-orange-600" />
                          Additional Months <span className="text-red-500">*</span>
                        </Label>
                        <Input
                         id="experienceMonths"
                          type="number"
                          min="0"
                          max="11"
                          value={formData.totalExperienceMonths === 0 ? "0" : formData.totalExperienceMonths || ""}
                          onChange={(e) => handleInputChange("totalExperienceMonths", e.target.value === "" ? "" : Number.parseInt(e.target.value))}
                          className={getInputClassName("totalExperienceMonths", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium")}
                        />
                        {showErrors && errors.totalExperienceMonths && (
                          <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                            <X className="h-4 w-4" />
                            {errors.totalExperienceMonths}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Skills Section */}
                <div className="mt-4">
                

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6" style={{ marginTop: "0px" }}>
                    <div className="space-y-4">
                      <Label className="text-sm font-semibold text-gray-800">Technical & Professional Skills</Label>
                      <CreatableSelect
                        isMulti
                        options={skillOptions}
                        value={formData.skills.map((skill) => ({ value: skill, label: skill }))}
                        onChange={(selected) => handleMultiSelectChange("skills", selected)}
                        onInputChange={(inputValue, actionMeta) => {
                          if (
                            actionMeta.action === "input-change" ||
                            actionMeta.action === "set-value"
                          ) {
                            setSkillsSearchTerm(inputValue);
                          } else if (
                            actionMeta.action === "menu-close" ||
                            actionMeta.action === "input-blur"
                          ) {
                            setSkillsSearchTerm("");
                          }
                        }}
                        placeholder="Select or search skills..."
                        isValidNewOption={(inputValue, selectValue, _selectOptions) => {
                          const trimmedValue = inputValue.trim();
                          if (!trimmedValue) return false;
                          if (selectValue.length >= MAX_SELECTABLE_ITEMS) return false;
                          return !selectValue.some(
                            (option) => option.label.toLowerCase() === trimmedValue.toLowerCase(),
                          );
                        }}
                        formatCreateLabel={(inputValue) => `Add "${inputValue.trim()}"`}
                        noOptionsMessage={() => "Type to add a custom skill"}
                        styles={selectStyles}
                        classNamePrefix="skills-select"
                      />
                      <div className="flex flex-wrap gap-3 mt-6">
                        {formData.skills.map((skill, index) => (
                          <Badge key={index} variant="secondary" className="gap-2 bg-orange-50 text-orange-700 border-2 text-xs border-orange-200 rounded-xl font-medium hover:bg-orange-100 transition-colors" style={{padding:"5px 10px"}}>
                            {skill}
                            <X
                              className="h-2 w-2 cursor-pointer hover:text-red-500 transition-colors"
                              onClick={() => removeFromArray("skills", index)}
                            />
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <Label className="text-sm font-semibold text-gray-800">Known Tools</Label>
                      <CreatableSelect
                        isMulti
                        options={toolOptions}
                        value={formData.knownTools.map((tool) => ({ value: tool, label: tool }))}
                        onChange={(selected) => handleMultiSelectChange("knownTools", selected)}
                        onInputChange={(inputValue, actionMeta) => {
                          if (
                            actionMeta.action === "input-change" ||
                            actionMeta.action === "set-value"
                          ) {
                            setToolsSearchTerm(inputValue);
                          } else if (
                            actionMeta.action === "menu-close" ||
                            actionMeta.action === "input-blur"
                          ) {
                            setToolsSearchTerm("");
                          }
                        }}
                        placeholder="Select known tools..."
                        isValidNewOption={(inputValue, selectValue, _selectOptions) => {
                          const trimmedValue = inputValue.trim();
                          if (!trimmedValue) return false;
                          if (selectValue.length >= MAX_SELECTABLE_ITEMS) return false;
                          return !selectValue.some(
                            (option) => option.label.toLowerCase() === trimmedValue.toLowerCase(),
                          );
                        }}
                        formatCreateLabel={(inputValue) => `Add "${inputValue.trim()}"`}
                        noOptionsMessage={() => "Type to add a custom tool"}
                        styles={selectStyles}
                        classNamePrefix="tools-select"
                      />
                      <div className="flex flex-wrap gap-3 mt-6">
                        {formData.knownTools.map((tool, index) => (
                          <Badge key={index} variant="secondary" className="gap-2 bg-orange-50 text-orange-700 border-2 text-xs border-orange-200 rounded-xl font-medium hover:bg-orange-100 transition-colors" style={{padding:"5px 10px"}}>
                            {tool}
                            <X
                              className="h-2 w-2 cursor-pointer hover:text-red-500 transition-colors"
                              onClick={() => removeFromArray("knownTools", index)}
                            />
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Career Preferences Section */}
                <div className="space-y-4" style={{marginTop:"20px"}}>
                 

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" style={{marginTop:"0px"}}>
                    {/* Job Categories */}
                    <div className="space-y-4">
                      <Label className="text-sm font-semibold text-gray-800">Preferred Industries & Job Categories</Label>
                      <Popover open={openCategoryDropdown} onOpenChange={setOpenCategoryDropdown}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={openCategoryDropdown}
                            className="h-12 sm:h-14 w-full justify-between rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium hover:bg-orange-50 hover:border-orange-300 transition-all duration-200"
                          >
                            <span className="text-gray-600">Select job categories...</span>
                            <ChevronDown className="ml-2 h-5 w-5 shrink-0 text-gray-500" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search categories..." className="h-12" />
                            <CommandEmpty>No categories found.</CommandEmpty>
                            <CommandGroup>
                              <CommandList className="max-h-60">
                                {categoryOptions.map((category) => (
                                  <CommandItem
                                    key={category.value}
                                    value={category.value}
                                    onSelect={() => handleCategorySelect(category.value)}
                                    className="cursor-pointer p-3"
                                  >
                                    <div className="flex items-center justify-between w-full">
                                      <span>{category.label}</span>
                                      {formData.preferredJobCategories.includes(category.value) && (
                                        <Check className="h-4 w-4 text-orange-600" />
                                      )}
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandList>
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <div className="flex flex-wrap gap-3 mt-4">
                        {formData.preferredJobCategories.map((categoryValue, index) => (
                          <Badge key={index} variant="secondary" className="gap-2 py-3 px-4 bg-orange-50 text-orange-700 border-2 border-orange-200 rounded-xl font-medium hover:bg-orange-100 transition-colors">
                            {getCategoryLabel(categoryValue)}
                            <X
                              className="h-4 w-4 cursor-pointer hover:text-red-500 transition-colors"
                              onClick={() => removeFromArray("preferredJobCategories", index)}
                            />
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Employment Types */}
                    <div className="space-y-4">
                      <Label className="text-sm font-semibold text-gray-800">Preferred Employment Types <span className="text-red-500">*</span></Label>
                      <Popover open={openEmploymentTypeDropdown} onOpenChange={setOpenEmploymentTypeDropdown}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={openEmploymentTypeDropdown}
                            className="h-12 sm:h-14 w-full justify-between rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 bg-white font-medium hover:bg-orange-50 hover:border-orange-300 transition-all duration-200"
                          >
                            <span className="text-gray-600">
                              {formData.preferredEmploymentTypes.length > 0 
                                ? `${formData.preferredEmploymentTypes.length} employment type(s) selected`
                                : "Select employment types..."
                              }
                            </span>
                            <ChevronDown className="ml-2 h-5 w-5 shrink-0 text-gray-500" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search employment types..." className="h-12" />
                            <CommandEmpty>No employment types found.</CommandEmpty>
                            <CommandGroup>
                              <CommandList>
                                {employmentTypeOptions.map((type) => (
                                  <CommandItem
                                    key={type}
                                    value={type}
                                    onSelect={() => handleEmploymentTypeSelect(type)}
                                    className="cursor-pointer p-3"
                                  >
                                    <div className="flex items-center justify-between w-full">
                                      <span>{type}</span>
                                      {formData.preferredEmploymentTypes.includes(type) && (
                                        <Check className="h-4 w-4 text-orange-600" />
                                      )}
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandList>
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <div className="flex flex-wrap gap-3 mt-4">
                        {formData.preferredEmploymentTypes.map((type, index) => (
                          <Badge key={index} variant="secondary" className="gap-2 py-3 px-4 bg-orange-50 text-orange-700 border-2 border-orange-200 rounded-xl font-medium hover:bg-orange-100 transition-colors">
                            {type}
                            <X
                              className="h-4 w-4 cursor-pointer hover:text-red-500 transition-colors"
                              onClick={() => removeFromArray("preferredEmploymentTypes", index)}
                            />
                          </Badge>
                        ))}
                      </div>
                      {showErrors && errors.preferredEmploymentTypes && (
                        <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                          <X className="h-4 w-4" />
                          {errors.preferredEmploymentTypes}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Work Types */}
                  <div className="space-y-4">
                    <Label className="text-sm font-semibold text-gray-800">Preferred Work Types <span className="text-red-500">*</span></Label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {(['remote', 'hybrid', 'on-site'] as const).map((type) => (
                        <div key={type} className="flex items-center space-x-4 p-5 border-2 border-gray-200 rounded-xl hover:bg-orange-50 hover:border-orange-300 transition-all duration-200 cursor-pointer">
                          <Checkbox
                            id={type}
                            checked={formData.preferredWorkTypes.includes(type)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFormData((prev) => ({
                                  ...prev,
                                  preferredWorkTypes: [...prev.preferredWorkTypes, type],
                                }));
                              } else {
                                setFormData((prev) => ({
                                  ...prev,
                                  preferredWorkTypes: prev.preferredWorkTypes.filter((t) => t !== type),
                                }));
                              }
                              if (checked && showErrors && errors.preferredWorkTypes) {
                                setErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors.preferredWorkTypes;
                                  return newErrors;
                                });
                              }
                            }}
                            className="w-6 h-6 rounded border-2 border-gray-300 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500 flex-shrink-0" 
                          />
                          <Label htmlFor={type} className="text-sm font-medium cursor-pointer capitalize flex-1 text-gray-700">
                            {type.replace("-", " ")}
                          </Label>
                        </div>
                      ))}
                    </div>
                    {showErrors && errors.preferredWorkTypes && (
                      <p className="text-red-600 text-sm mt-2 flex items-center gap-2">
                        <X className="h-4 w-4" />
                        {errors.preferredWorkTypes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {/* <div className="space-y-4 sm:space-y-6 lg:space-y-8">
                
              </div> */}
            </CardContent>
          </Card>

          {/* Professional Information Card */}

          {/* Submit Button */}
          <div className="flex justify-end items-center sticky bottom-4 mr-8">
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="gap-4 p-4 h-auto rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 border-0"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-6 w-6 animate-spin" />
                  Creating Profile...
                </>
              ) : (
                <>
                  Create Profile
                  <Plus className="h-6 w-6" />
                </>
              )}
            </Button>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 text-green-800 rounded-2xl flex items-center gap-4 shadow-lg">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-lg">Success!</h4>
                <p className="font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Loading Overlay */}
          {isSubmitting && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="bg-white rounded-3xl p-10 shadow-2xl max-w-md mx-4 text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="h-10 w-10 text-white animate-spin" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Creating Profile...</h3>
                <p className="text-gray-600 mb-6 text-lg">
                  We're processing your candidate information and setting up their profile.
                </p>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-gradient-to-r from-orange-500 to-amber-500 h-3 rounded-full animate-pulse" style={{ width: '75%' }}></div>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  You'll be redirected to view candidates shortly...
                </p>
              </div>
            </div>
          )}
        </form>

        {/* Interview Scheduling Modal */}
        <Dialog open={showInterviewModal} onOpenChange={setShowInterviewModal}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Calendar className="h-4 w-5 text-orange-600" />
                Schedule Interview
              </DialogTitle>
              <DialogDescription className="text-base pt-2">
                Would you like to schedule the 1st round interview for this candidate?
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Checkbox to enable scheduling */}
              <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                <Checkbox 
                  id="schedule-interview" 
                  checked={scheduleInterview}
                  onCheckedChange={(checked) => setScheduleInterview(checked as boolean)}
                  className="h-5 w-5"
                />
                <label
                  htmlFor="schedule-interview"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Yes, I want to schedule the 1st round interview
                </label>
              </div>

              {/* Add your form here when checkbox is checked */}
              {scheduleInterview && (
                <div className="space-y-4 animate-in fade-in-50 duration-300">
                  <div className="w-full sm:p-4 rounded-xl mt-1">

                    {/* Form Fields */}
                    <div className="space-y-2">
                      {/* Interview Type Toggle */}
                      <div className="mb-5 w-full lg:w-[70%]">
                        <Label className="text-sm font-semibold text-gray-800 mb-3 block">
                          Interview Type <span className="text-red-500">*</span>
                        </Label>
                        <div className="flex items-center gap-5">
                          <div className="relative inline-flex bg-gray-100 rounded-xl p-1 shadow-sm">
                            <button
                              type="button"
                              onClick={async () => {
                                setScheduleFormData((prev) => ({ ...prev, interviewType: "Offline" }))

                              }}
                              className={`relative px-6 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 ${scheduleFormData.interviewType === "Offline"
                                  ? "bg-orange-600 text-white shadow-md"
                                  : "text-gray-600 hover:text-gray-900"
                                }`}
                            >
                              <div className="flex items-center gap-2">Offline</div>
                            </button>
                            <button
                              type="button"
                              onClick={() => setScheduleFormData((prev) => ({ ...prev, interviewType: "Online" }))}
                              className={`relative px-6 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 ${scheduleFormData.interviewType === "Online"
                                  ? "bg-orange-600 text-white shadow-md"
                                  : "text-gray-600 hover:text-gray-900"
                                }`}
                            >
                              <div className="flex items-center gap-2">Online</div>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Date & Timezone Row */}
                      <div className="grid w-full lg:w-[70%]">
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-gray-800">
                            Interview Date <span className="text-red-500">*</span>
                          </Label>
                          <div className="relative">
                            <DatePicker
                              selected={scheduleFormData.scheduledDate ? new Date(scheduleFormData.scheduledDate) : null}
                              onChange={(date) => {
                                const formattedDate = date ? format(date, "yyyy-MM-dd") : ""
                                console.log("Selected date:", formattedDate)
                                setScheduleFormData((prev) => ({ ...prev, scheduledDate: formattedDate }))
                                // Real-time validation for date
                                setTimeout(() => {
                                  validateDate(formattedDate)
                                }, 100)
                              }}
                              minDate={new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)} // 10 days ago
                              dateFormat="MMMM d, yyyy"
                              placeholderText="Select interview date"
                              className="w-full p-3 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
                              wrapperClassName="w-full"
                              calendarClassName="shadow-lg border border-gray-200 rounded-lg"
                            />
                            <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" style={{ color: 'black' }} />
                          </div>
                        </div>

                        {/* <div className="space-y-2 max-w-[400px]">
            <Label className="text-sm font-semibold text-gray-800">Time Zone</Label>
            <Select
              value={scheduleFormData.timeZone}
              onValueChange={(value) => setScheduleFormData((prev) => ({ ...prev, timeZone: value }))}
            >
              <SelectTrigger className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 shadow-sm">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Asia/Kolkata">India Standard Time (UTC+5:30)</SelectItem>
                <SelectItem value="UTC">UTC</SelectItem>
                <SelectItem value="America/New_York">EST (Eastern Time)</SelectItem>
                <SelectItem value="Europe/London">GMT (London)</SelectItem>
                <SelectItem value="America/Los_Angeles">PST (Pacific Time)</SelectItem>
              </SelectContent>
            </Select>
          </div> */}
                      </div>

                      {/* Start & End Time Row */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-6 w-full lg:w-[70%]">
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-gray-800">
                            Start Time <span className="text-red-500">*</span>
                          </Label>
                          <div className="relative">
                            <input
                              type="time"
                              value={scheduleFormData.startTime}
                              onChange={(e) => {
                                const newStartTime = e.target.value;
                                setScheduleFormData((prev) => ({ ...prev, startTime: newStartTime }));
                                // Real-time validation for start time
                                setTimeout(() => {
                                  // Check if start time is less than end time
                                  if (scheduleFormData.endTime && newStartTime >= scheduleFormData.endTime) {
                                    setValidationErrors((prev) => ({ ...prev, startTime: true }));
                                    toast.error("Start < End", { duration: 1500, style: { whiteSpace: 'nowrap' } });
                                  } else {
                                    setValidationErrors((prev) => ({ ...prev, startTime: false }));
                                  }
                                }, 100);
                              }}
                              className={`max-w-[400px] w-full p-3 border rounded-lg text-sm text-black-700 focus:outline-none focus:ring-2 shadow-sm ${validationErrors.startTime
                                  ? "border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50"
                                  : "border-gray-200 focus:ring-orange-500 focus:border-orange-500"
                                }`}
                              step="900"
                              required
                            />
                          </div>
                          {validationErrors.startTime && (
                            <div className="flex items-center gap-2 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                              <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                              <span className="text-xs text-red-600">Please select a valid start time</span>
                            </div>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-gray-800">
                            End Time <span className="text-red-500">*</span>
                          </Label>
                          <div className="relative">
                            <input
                              type="time"
                              value={scheduleFormData.endTime}
                              onChange={(e) => {
                                const newEndTime = e.target.value;
                                setScheduleFormData((prev) => ({ ...prev, endTime: newEndTime }));
                                // Real-time validation for end time
                                setTimeout(() => {
                                  // Check if start time is less than end time
                                  if (scheduleFormData.startTime && scheduleFormData.startTime >= newEndTime) {
                                    setValidationErrors((prev) => ({ ...prev, endTime: true }));
                                    toast.error("End > Start", { duration: 1500, style: { whiteSpace: 'nowrap' } });
                                  } else {
                                    setValidationErrors((prev) => ({ ...prev, endTime: false }));
                                  }
                                }, 100);
                              }}
                              className={`max-w-[400px] w-full p-3 border rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 shadow-sm ${validationErrors.endTime
                                  ? "border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50"
                                  : "border-gray-200 focus:ring-orange-500 focus:border-orange-500"
                                }`}
                              step="900"
                              required
                            />
                          </div>
                          {validationErrors.endTime && (
                            <div className="flex items-center gap-2 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                              <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                              <span className="text-xs text-red-600">End time must be after start time</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Meeting Subject and Additional Interviewers Row */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-6 w-full lg:w-[70%]">
                        {/* Meeting Subject */}
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-gray-800">Interviewer</Label>
                          <Input
                            value={`Interview - ${user?.username || user?.email || "Admin"}`}
                            readOnly
                            className="max-w-[400px] w-full p-3 border border-gray-200 rounded-lg text-sm text-gray-700 bg-gray-50 shadow-sm"
                          />
                        </div>

                        {/* Additional Interviewers */}
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-gray-800">Additional Interviewers</Label>
                          <div className="flex gap-2">
                            <Input
                              type="email"
                              value={emailInput}
                              onChange={(e) => setEmailInput(e.target.value)}
                              onKeyPress={handleEmailKeyPress}
                              placeholder="Enter email address and press Enter"
                              className="max-w-[400px] flex-1 p-3 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                            />
                            <Button
                              type="button"
                              onClick={handleAddEmail}
                              className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-lg shadow-sm"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>

                          {/* Display added emails - Below the input field */}
                          {scheduleFormData.additionalParticipants.length > 0 && (
                            <div className="space-y-2 mt-3">
                              <Label className="text-xs text-gray-600">Added Email Addresses:</Label>
                              <div className="flex flex-wrap gap-2">
                                {scheduleFormData.additionalParticipants.map((email, index) => (
                                  <div
                                    key={index}
                                    className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1 shadow-sm"
                                  >
                                    <Mail className="h-3 w-3 text-blue-600" />
                                    <span className="text-sm text-gray-800">{email}</span>
                                    <Button
                                      type="button"
                                      onClick={() => handleRemoveEmail(email)}
                                      variant="ghost"
                                      size="sm"
                                      className="h-4 w-4 p-0 hover:bg-red-100 text-gray-500 hover:text-red-600"
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Location Field - Only show for offline interviews */}
                      {scheduleFormData.interviewType === "Offline" && (
                        <div className="space-y-2w-full lg:w-[70%]">
                          <Label className="text-sm font-semibold text-gray-800">
                            Interview Location <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            type="text"
                            value={scheduleFormData.location || ""}
                            onChange={(e) => setScheduleFormData((prev) => ({ ...prev, location: e.target.value }))}
                            placeholder="Enter the physical location for the interview"
                            className="max-w-[570px] w-full p-3 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                            required
                          />
                          <div className="flex items-start gap-2 mt-1">
                            <MapPin className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-gray-500">
                              Please provide the complete address including building name, floor, and room number if applicable.
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowInterviewModal(false);
                  setScheduleInterview(false);
                  // Reset form data
                  setScheduleFormData({
                    interviewType: "Offline",
                    scheduledDate: "",
                    startTime: "",
                    endTime: "",
                    timeZone: "UTC",
                    interviewers: [],
                    notes: "",
                    additionalParticipants: [],
                    location: "",
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  // Validate if scheduling is enabled
                  if (scheduleInterview) {
                    console.log("Validating schedule form data:", scheduleFormData);
                    
                    if (!scheduleFormData.scheduledDate || !scheduleFormData.startTime || !scheduleFormData.endTime) {
                      toast.error("Please fill in all required interview fields (Date, Start Time, End Time)");
                      return;
                    }
                    
                    if (scheduleFormData.interviewType === "Offline" && !scheduleFormData.location) {
                      toast.error("Please provide a location for offline interview");
                      return;
                    }
                  }
                  
                  setShowInterviewModal(false);
                  handleActualSubmit();
                }}
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
              >
                Continue
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <BlockedPhonePopup
          open={blockedPopupOpen}
          message={blockedPopupMessage}
          onClose={() => setBlockedPopupOpen(false)}
        />
      </div>
    </div>
  );
}





// import type React from "react";
// import { toast } from "react-hot-toast";
// import { useState, useCallback, useRef, useEffect } from "react";
// import { Button } from "../../components/ui/button";
// import { Input } from "../../components/ui/input";
// import { Label } from "../../components/ui/label";
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
// import { Textarea } from "../../components/ui/textarea";
// import { Badge } from "../../components/ui/badge";
// import { Checkbox } from "../../components/ui/checkbox";
// import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
// import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
// import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
// import { Plus, X, UploadCloud, ArrowRight, ArrowLeft, ArrowLeftCircle, User, Briefcase, CheckCircle, MapPin, Phone, Mail, Calendar, FileText, Languages, Award, Target, Building, Clock, Loader2, Search, ChevronDown, Check } from 'lucide-react';
// import { createApplication, type ICreateApplicationFormData } from "../../services/candidateapi";
// import {  getJobsForSelection } from "../../services/jobsapi";
// import Cookies from "js-cookie";
// import { useNavigate } from "react-router-dom";
// import { useParams } from "react-router-dom";
// import { getCandidateById } from "../../services/candidateapi";

// // Job interface for API response
// interface Job {
//   _id: string;
//   title: string;
//   companyName: string;
//   company: string;
//   location: string;
//   status: string;
//   workType?: string;
//   id?: string;
// }

// interface GetJobsResponse {
//   jobs: Job[];
//   totalJobs: number;
//   totalPages: number;
//   currentPage: number;
// }

// // Update the CandidateFormData interface to match backend expectations
// interface CandidateFormData {
//   // --- Job Details (Required) ---
//   jobId: string;
//   jobType: 'main' | 'sub';
//   // --- Candidate Details (All required as per backend schema) ---
//   name: string;
//   email: string;
//   phone: string;
//   fatherName: string;
//   dateOfBirth: string; // ISO 8601 date string
//   gender: 'Male' | 'Female' | 'Other';
//   aadharNumber?: string;
//   highestQualification: string;
//   currentLocationDetails: {
//     street: string;
//     area: string;
//     city: string;
//     pincode: string;
//     fullAddress: string;
//   };
//   spokenLanguages: string[];
//   totalExperienceYears: number;
//   totalExperienceMonths: number;
//   skills: string[];
//   preferredJobCategories: string[];
//   preferredEmploymentTypes: string[];
//   preferredWorkTypes: ('remote' | 'hybrid' | 'on-site')[];
// }

// interface AddCandidateFormProps {
//   onBack: () => void;
//   onSubmit?: (data: CandidateFormData) => void;
//   refreshCandidates?: () => void;
// }

// export default function AddCandidateForm({ onBack, onSubmit, refreshCandidates }: AddCandidateFormProps) {
//   const navigate = useNavigate();
//   const { id } = useParams();
//   const apiClient = createApplication;
//   const [currentStep, setCurrentStep] = useState(1);
//   const formTopRef = useRef<HTMLDivElement>(null);
//   const [formData, setFormData] = useState<CandidateFormData>({
//     name: "",
//     email: "",
//     phone: "",
//     fatherName: "",
//     dateOfBirth: "",
//     gender: "Male",
//     aadharNumber: "",
//     highestQualification: "",
//     currentLocationDetails: {
//       street: "",
//       area: "",
//       city: "",
//       pincode: "",
//       fullAddress: "",
//     },
//     spokenLanguages: [],
//     totalExperienceYears: 0,
//     totalExperienceMonths: 0,
//     skills: [],
//     preferredJobCategories: [],
//     preferredEmploymentTypes: [],
//     preferredWorkTypes: [],
//     jobId: "",
//     jobType: "main",
//   });

//   // Fetch candidate details if editing
//   useEffect(() => {
//     if (id) {
//       (async () => {
//         try {
//           const candidate = await getCandidateById(id);
//           if (candidate) {
//             setFormData({
//               ...formData,
//               ...candidate,
//               currentLocationDetails: candidate.currentLocationDetails || formData.currentLocationDetails,
//               skills: candidate.skills || [],
//               spokenLanguages: candidate.spokenLanguages || [],
//               preferredJobCategories: candidate.preferredJobCategories || [],
//               preferredEmploymentTypes: candidate.preferredEmploymentTypes || [],
//               preferredWorkTypes: candidate.preferredWorkTypes || [],
//             });
//           }
//         } catch (error) {
//           console.error("Failed to fetch candidate details", error);
//         }
//       })();
//     }
//   }, [id]);

//   const [currentSkill, setCurrentSkill] = useState("");
//   const [currentJobCategory, setCurrentJobCategory] = useState("");
//   const [selectedLanguage, setSelectedLanguage] = useState("");
//   const [resumeFileName, setResumeFileName] = useState<string | null>(null);
//   const fileInputRef = useRef<HTMLInputElement>(null);
//   const [successMessage, setSuccessMessage] = useState<string | null>(null);
//   const performingUser = { email: Cookies.get("userEmail") || "Unknown" };
//   const [errors, setErrors] = useState<Record<string, string>>({});
//   const [showErrors, setShowErrors] = useState(false);
//   const [isSubmitting, setIsSubmitting] = useState(false);

//   // Jobs state
//   const [jobs, setJobs] = useState<Job[]>([]);
//   const [loadingJobs, setLoadingJobs] = useState(false);
//   const [jobsError, setJobsError] = useState<string | null>(null);
//   const [jobSearchTerm, setJobSearchTerm] = useState("");

//   // Searchable dropdown states
//   const [openCategoryDropdown, setOpenCategoryDropdown] = useState(false);
//   const [openEmploymentTypeDropdown, setOpenEmploymentTypeDropdown] = useState(false);

//   // Qualification options with modern names
//   const qualificationOptions = [
//     { value: "10th", label: "Secondary Education (10th Grade)" },
//     { value: "12th", label: "Higher Secondary Education (12th Grade)" },
//     { value: "ITI", label: "Industrial Training Institute (ITI)" },
//     { value: "Diploma", label: "Diploma" },
//     { value: "Graduation (10 + 2 + 3)", label: "Bachelor's Degree (3 Years)" },
//     { value: "Graduation (10 + 2 + 4)", label: "Bachelor's Degree (4 Years)" },
//     { value: "Post Graduation", label: "Master's Degree" },
//     { value: "PhD", label: "Doctorate (PhD)" },
//   ];

//   const steps = [
//     {
//       id: 1,
//       title: "Personal & Contact",
//       icon: User,
//       description: "Basic information and contact details",
//     },
//     {
//       id: 2,
//       title: "Professional & Preferences",
//       icon: Briefcase,
//       description: "Skills, experience and job preferences",
//     },
//   ];

//   // Fetch jobs on component mount
//   useEffect(() => {
//     let isCancelled = false;

//     const fetchJobs = async () => {
//       if (isCancelled) return;
      
//       setLoadingJobs(true);
//       setJobsError(null);
//       try {
//         // Use the selection endpoint which bypasses role restrictions
//         const response = await getJobsForSelection({
//           _t: Date.now()
//         });
        
//         if (isCancelled) return; // Don't update state if component unmounted
        
//         console.log('✅ Jobs for selection fetched successfully:', response);
        
//         // Based on the backend response structure: response.data.jobs
//         let jobsArray = [];
//         if (response && response.data && Array.isArray(response.data.jobs)) {
//           jobsArray = response.data.jobs;
//           console.log('✅ Using response.data.jobs:', jobsArray);
//         } else if (response && Array.isArray(response as any)) {
//           jobsArray = response as any;
//           console.log('✅ Using direct response as array:', jobsArray);
//         } else if (response && Array.isArray(response.data)) {
//           jobsArray = response.data;
//           console.log('✅ Using response.data:', jobsArray);
//         } else if (Array.isArray(response)) {
//           jobsArray = response;
//           console.log('✅ Using direct response:', jobsArray);
//         } else {
//           console.warn('⚠️ Unexpected response structure:', response);
//           jobsArray = [];
//         }
        
//         console.log('✅ Final jobs array length:', jobsArray.length);
//         console.log('✅ Sample job:', jobsArray[0]);
//         setJobs(jobsArray);
//       } catch (error) {
//         if (isCancelled) return;
//         console.error('❌ Error fetching jobs for selection:', error);
//         setJobsError('Failed to load jobs. Please try again.');
//       } finally {
//         if (!isCancelled) {
//           setLoadingJobs(false);
//         }
//       }
//     };

//     fetchJobs();

//     // Cleanup function to prevent state updates if component unmounts
//     return () => {
//       isCancelled = true;
//     };
//   }, []);

//   // Effect to scroll to top when step changes
//   useEffect(() => {
//     // Don't scroll on initial load (step 1)
//     if (currentStep > 1) {
//       // Try multiple scroll methods for better compatibility
//       if (formTopRef.current) {
//         formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
//       } else {
//         // Fallback methods
//         window.scrollTo({ top: 0, behavior: 'smooth' });
//         document.body.scrollTop = 0; // For Safari
//         document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
//       }
//     }
//   }, [currentStep]);

//   // Filter jobs based on search term
//   const filteredJobs = jobs.filter(job => 
//     job.title?.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
//     job.company?.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
//     job.location?.toLowerCase().includes(jobSearchTerm.toLowerCase())
//   );

//   const handleInputChange = (field: string, value: any) => {
//     setFormData((prev) => {
//       const newFormData = { ...prev } as any;
//       if (field.includes(".")) {
//         const [parent, child] = field.split(".");
//         newFormData[parent] = { ...newFormData[parent], [child]: value };
//       } else {
//         newFormData[field] = value;
//       }
//       return newFormData as CandidateFormData;
//     });
    
//     // Clear errors for this field when user types
//     if (showErrors) {
//       setErrors((prev) => {
//         const newErrors = { ...prev };
        
//         // Clear error for the exact field name
//         if (errors[field]) {
//           delete newErrors[field];
//         }
        
//         // Handle nested field errors (like currentLocationDetails.city -> city)
//         if (field.includes(".")) {
//           const [parent, child] = field.split(".");
//           // Clear both the nested field and the simplified field name
//           delete newErrors[child];
//           delete newErrors[`${parent}.${child}`];
//         }
        
//         // Special handling for specific field mappings
//         if (field === "currentLocationDetails.city") {
//           delete newErrors["city"];
//         }
        
//         return newErrors;
//       });
//     }
//   };

//   const commonLanguages = [
//     "English",
//     "Hindi",
//     "Bengali",
//     "Telugu",
//     "Marathi",
//     "Tamil",
//     "Gujarati",
//     "Kannada",
//     "Malayalam",
//     "Odia",
//     "Punjabi",
//     "Assamese",
//     "Maithili",
//     "Urdu",
//     "Nepali",
//     "Konkani",
//   ];

//   const categoryOptions = [
//     'Aviation',
//     'Banking',
//     'Insurance',
//     'Oil And Gas',
//     'Retail',
//     'Education',
//     'Consumer Goods',
//     'Manufacturing',
//     'Information Technology',
//     'Health Care',
//     'BPO',
//     'ITES',
//     'Entertainment',
//     'Finance',
//     'Textile',
//     'Media and news',
//     'Food processing',
//     'Hospitality',
//     'Construction',
//     'Law',
//     'Advertising',
//     'E-commerce',
//     'Other'
//   ];

//   const employmentTypeOptions = [
//     'Full-time',
//     'Part-time', 
//     'Contract',
//     'Freelance',
//     'Internship'
//   ];

//   const addToArray = (
//     fieldName: keyof CandidateFormData,
//     item: string,
//     setItem: React.Dispatch<React.SetStateAction<string>>,
//   ) => {
//     if (
//       item.trim() !== "" &&
//       Array.isArray(formData[fieldName]) &&
//       !(formData[fieldName] as string[]).includes(item.trim())
//     ) {
//       setFormData((prev) => ({
//         ...prev,
//         [fieldName]: [...(prev[fieldName] as string[]), item.trim()],
//       }));
//       setItem("");
      
//       // Clear error when an item is added to array fields
//       if (showErrors && errors[fieldName as string]) {
//         setErrors((prev) => {
//           const newErrors = { ...prev };
//           delete newErrors[fieldName as string];
//           return newErrors;
//         });
//       }
//     }
//   };

//   const removeFromArray = (fieldName: keyof CandidateFormData, index: number) => {
//     setFormData((prev) => {
//       const newArray = [...(prev[fieldName] as string[])];
//       newArray.splice(index, 1);
//       return {
//         ...prev,
//         [fieldName]: newArray,
//       };
//     });
//   };

//   // Helper functions for searchable dropdowns
//   const handleCategorySelect = (category: string) => {
//     if (!formData.preferredJobCategories.includes(category)) {
//       setFormData((prev) => ({
//         ...prev,
//         preferredJobCategories: [...prev.preferredJobCategories, category]
//       }));
      
//       // Clear error when a category is added
//       if (showErrors && errors.preferredJobCategories) {
//         setErrors((prev) => {
//           const newErrors = { ...prev };
//           delete newErrors.preferredJobCategories;
//           return newErrors;
//         });
//       }
//     }
//     setOpenCategoryDropdown(false);
//   };

//   const handleEmploymentTypeSelect = (type: string) => {
//     if (!formData.preferredEmploymentTypes.includes(type)) {
//       setFormData((prev) => ({
//         ...prev,
//         preferredEmploymentTypes: [...prev.preferredEmploymentTypes, type]
//       }));
//     } else {
//       // Remove if already selected
//       setFormData((prev) => ({
//         ...prev,
//         preferredEmploymentTypes: prev.preferredEmploymentTypes.filter(t => t !== type)
//       }));
//     }
    
//     // Clear error when first employment type is selected
//     if (showErrors && errors.preferredEmploymentTypes && formData.preferredEmploymentTypes.length === 0) {
//       setErrors((prev) => {
//         const newErrors = { ...prev };
//         delete newErrors.preferredEmploymentTypes;
//         return newErrors;
//       });
//     }
//   };

//   const [resumeFile, setResumeFile] = useState<File | null>(null);

//   const handleResumeUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
//     const file = e.target.files?.[0];
//     if (file) {
//       setResumeFileName(file.name);
//       setResumeFile(file);
//     }
//   };

//   const clearResume = () => {
//     setResumeFileName(null);
//     setResumeFile(null);
//     if (fileInputRef.current) {
//       fileInputRef.current.value = "";
//     }
//   };

//   const validateAadhar = (aadhar: string): boolean => {
//     const aadharRegex = /^\d{12}$/;
//     return aadharRegex.test(aadhar.replace(/\s/g, ''));
//   };

//   const validatePhone = (phone: string): boolean => {
//     // Remove spaces and hyphens for validation
//     const cleanPhone = phone.replace(/[\s-]/g, '');
    
//     // Allow phone numbers with or without +91/91 prefix
//     // Should be 10 digits (without prefix) or 12/13 digits (with prefix)
//     const phoneRegex = /^(\+91|91)?[0-9]{10}$/;
    
//     // Additional check: if it's a 10-digit number, it should not start with 0 or 1
//     if (cleanPhone.length === 10) {
//       return /^[1-9]\d{9}$/.test(cleanPhone);
//     }
    
//     // If it has country code, extract the 10-digit part and validate
//     if (cleanPhone.startsWith('+91') && cleanPhone.length === 13) {
//       const phoneNumber = cleanPhone.substring(3);
//       return /^[2-9]\d{9}$/.test(phoneNumber);
//     }
    
//     if (cleanPhone.startsWith('91') && cleanPhone.length === 12) {
//       const phoneNumber = cleanPhone.substring(2);
//       return /^[2-9]\d{9}$/.test(phoneNumber);
//     }
    
//     return phoneRegex.test(cleanPhone);
//   };

//   const validateEmail = (email: string): boolean => {
//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     return emailRegex.test(email);
//   };

//   const validateStep1 = (): { isValid: boolean; errors: Record<string, string> } => {
//     const newErrors: Record<string, string> = {};

//     if (!formData.name.trim()) newErrors.name = "Full name is required";
//     if (!formData.fatherName.trim()) newErrors.fatherName = "Father's name is required";
//     if (!formData.email.trim()) {
//       newErrors.email = "Email is required";
//     } else if (!validateEmail(formData.email)) {
//       newErrors.email = "Please enter a valid email address";
//     }
//     if (!formData.phone.trim()) {
//       newErrors.phone = "Phone number is required";
//     } else if (!validatePhone(formData.phone)) {
//       newErrors.phone = "Please enter a valid 10-digit phone number";
//     }
//     if (!formData.jobId.trim()) newErrors.jobId = "Job selection is required";
//     if (!formData.dateOfBirth) {
//       newErrors.dateOfBirth = "Date of birth is required";
//     } else {
//       // Check if candidate is at least 16 years old
//       const today = new Date();
//       const birthDate = new Date(formData.dateOfBirth);
//       const age = today.getFullYear() - birthDate.getFullYear();
//       const monthDiff = today.getMonth() - birthDate.getMonth();
      
//       // Calculate exact age considering month and day
//       const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) 
//         ? age - 1 
//         : age;
      
//       if (actualAge < 16) {
//         newErrors.dateOfBirth = "Candidate must be at least 16 years old";
//       }
//     }
//     if (!formData.currentLocationDetails.city.trim()) newErrors.city = "City is required";
//     if (formData.spokenLanguages.length === 0) newErrors.spokenLanguages = "At least one language is required";

//     return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
//   };

//   const validateStep2 = (): { isValid: boolean; errors: Record<string, string> } => {
//     const newErrors: Record<string, string> = {};

//     if (!formData.highestQualification.trim()) newErrors.highestQualification = "Educational qualification is required";
    
//     // Validate work experience - both years and months are required
//     if (formData.totalExperienceYears === undefined ) {
//       newErrors.totalExperienceYears = "Work experience (years) is required";
//     }
    
//     // Validate months - now required
//     if (formData.totalExperienceMonths === undefined || formData.totalExperienceMonths > 11) {
//       newErrors.totalExperienceMonths = "Additional months is required and should be between 0-11";
//     }
    
//     if (formData.preferredEmploymentTypes.length === 0) newErrors.preferredEmploymentTypes = "At least one employment type is required";
//     if (formData.preferredWorkTypes.length === 0) newErrors.preferredWorkTypes = "At least one work type is required";

//     return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
//   };

// const handleNext = () => {
//   if (currentStep === 1) {
//     // Validate Step 1 before proceeding
//     const validation = validateStep1();
//     if (!validation.isValid) {
//       setErrors(validation.errors);
//       setShowErrors(true);
//       // Scroll to first error field
//       const firstErrorField = Object.keys(validation.errors)[0];
//       const element = document.getElementById(firstErrorField);
//       if (element) {
//         element.scrollIntoView({ behavior: 'smooth', block: 'center' });
//         element.focus();
//       }
//       return; // Don't proceed if validation fails
//     }

//     // Check for duplicate phone/email before moving to next step
//     (async () => {
//       try {
//         const { getAllApplications } = await import("../../services/candidateapi");
//         const allAppsResponse = await getAllApplications({});
//         const allApps = allAppsResponse?.data?.applications || [];
//         const phoneExists = allApps.some((app) => app.candidate?.phone === formData.phone);
//         if (phoneExists) {
//           toast.error("This mobile number is already registered in an application. Please use a different number.");
//           return;
//         }
//         const emailExists = allApps.some((app) => app.candidate?.email === formData.email);
//         if (emailExists) {
//           toast.error("This email is already registered in an application. Please use a different email.");
//           return;
//         }
//         // If no duplicates, move to next step
//         setCurrentStep(2);
//         setErrors({});
//         setShowErrors(false);
//       } catch (err) {
//         toast.error("Error checking duplicates. Please try again.");
//       }
//     })();
//   }
// };

//   // Add missing handlePrevious function
//   const handlePrevious = () => {
//     if (currentStep > 1) {
//       setCurrentStep(currentStep - 1);
//       setErrors({});
//       setShowErrors(false);
//     }
//   };

//   const handleSubmit = useCallback(
//     async (e: React.FormEvent) => {
//       e.preventDefault();
      
//       // Only validate and submit when we're on the final step (step 2)
//       if (currentStep !== 2) {
//         return; // Don't submit if not on final step
//       }

//       const validation = validateStep2();
//       if (!validation.isValid) {
//         setErrors(validation.errors);
//         setShowErrors(true);
//         const firstErrorField = Object.keys(validation.errors)[0];
//         const element = document.getElementById(firstErrorField);
//         if (element) {
//           element.scrollIntoView({ behavior: 'smooth', block: 'center' });
//           element.focus();
//         }
//         return;
//       }

//       // Start loading state
//       setIsSubmitting(true);

//       // Fetch all applications and check for duplicate phone number before submitting
//       try {
//         const { getAllApplications } = await import("../../services/candidateapi");
//         const allAppsResponse = await getAllApplications({});
//   const allApps: any[] = allAppsResponse?.data?.applications || [];

//         const phoneExists = allApps.some((app: any) => app.candidate?.phone === formData.phone);
//         if (phoneExists) {
//           toast.error("This mobile number is already registered in an application. Please use a different number.");
//           setIsSubmitting(false);
//           return;
//         }

//         const emailExists = allApps.some((app: any) => app.candidate?.email === formData.email);
//         if (emailExists) {
//           toast.error("This email is already registered in an application. Please use a different email.");
//           setIsSubmitting(false);
//           return;
//         }

//         // Create normalized data without any status field - let backend handle initial status
//         const normalizedData: ICreateApplicationFormData = {
//           jobId: formData.jobId,
//           jobType: formData.jobType,
//           name: formData.name,
//           email: formData.email,
//           phone: formData.phone,
//           fatherName: formData.fatherName,
//           dateOfBirth: formData.dateOfBirth,
//           gender: formData.gender,
//           ...(formData.aadharNumber && formData.aadharNumber.trim() && { aadharNumber: formData.aadharNumber }),
//           highestQualification: formData.highestQualification,
//           currentLocationDetails: formData.currentLocationDetails,
//           totalExperienceYears: formData.totalExperienceYears,
//           totalExperienceMonths: formData.totalExperienceMonths,
//           skills: formData.skills.length > 0 ? formData.skills : ["General"],
//           spokenLanguages: formData.spokenLanguages.length > 0 ? formData.spokenLanguages : ["English"],
//           preferredJobCategories: formData.preferredJobCategories.length > 0 ? formData.preferredJobCategories : ["General"],
//           preferredEmploymentTypes: formData.preferredEmploymentTypes.length > 0 ? formData.preferredEmploymentTypes : ["Full-time"],
//           preferredWorkTypes: formData.preferredWorkTypes.length > 0 ? formData.preferredWorkTypes : ["on-site"]
//           // Remove the resume field for now since we're sending JSON
//         };

//         console.log("Submitting candidate data:", normalizedData);

//         // Call the API without the resume file for now
//         await apiClient(normalizedData);
        
//         setSuccessMessage(
//           `New candidate profile created for ${formData.email} by ${performingUser.email}`
//         );

//         if (onSubmit) {
//           onSubmit(formData);
//         }

//         if (refreshCandidates) {
//           refreshCandidates();
//         }

//         // Show loading for navigation with better UX messaging
//         setTimeout(() => {
//           setIsSubmitting(false);
//           navigate("/view-applications");
//         }, 2000);
//       } catch (error: any) {
//         setIsSubmitting(false); // Stop loading on error
//         console.error("Error creating application:", error.message);
//         // Add better error handling
//         if (error.response?.data?.message) {
//           // Show toast for duplicate email or phone
//           if (error.response.data.message.includes("duplicate") || error.response.data.message.includes("already exists")) {
//             if (error.response.data.message.toLowerCase().includes("email")) {
//               toast.error("This email is already registered. Please use a different email.");
//             }
//             if (error.response.data.message.toLowerCase().includes("phone") || error.response.data.message.toLowerCase().includes("mobile")) {
//               toast.error("This mobile number is already registered. Please use a different number.");
//             }
//           }
//           console.error("Backend error:", error.response.data.message);
//         }
//       }
//     },
//     [formData, onSubmit, refreshCandidates, navigate, apiClient, performingUser.email, currentStep],
//   );

//   const getInputClassName = (fieldName: string, baseClassName: string = "") => {
//     const hasError = showErrors && errors[fieldName];
//     const errorClasses = hasError ? "border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50" : "";
//     return `${baseClassName} ${errorClasses}`.trim();
//   };

//   return (
//     <div className="min-h-screen ">
//       <div className="w-full px-6 py-6">
//         {/* Combined Header and Title Section */}
//         <div ref={formTopRef} className="mb-5 flex items-center gap-4">
//           {/* Back Button */}
//           <div>
//             <Button
//               variant="ghost"
//               size="lg"
//               className="p-2 rounded-full hover:bg-gray-100 transition-colors"
//               onClick={() => navigate(-1)}
//             >
//               <ArrowLeftCircle className="h-9 w-9 text-gray-600" style={{width: "30px", height: "30px"}}/>
//             </Button>
//           </div>
          
//           {/* Title Section */}
//           <div className="flex-grow">
//             <h1 className="text-md lg:text-2xl font-bold text-gray-900">Add New Candidate</h1>
//             <p className="text-m text-gray-600 font-medium">Create a comprehensive candidate profile with detailed information</p>
//           </div>
//         </div>
//         <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 lg:space-y-8">
//             <Card className="shadow-2xl rounded-3xl border-0 bg-white/80 backdrop-blur-sm overflow-hidden w-full">
//               <CardContent className="p-8 space-y-4 sm:space-y-6 lg:space-y-8">
//                 {/* Basic Information Section */}
//                 <div className="space-y-6">
//                   <div className="flex items-center gap-3 pb-4 border-b">
//                     <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center">
//                       <User className="h-4 w-4 text-orange-600" />
//                     </div>
//                     <h3 className="text-md font-bold text-gray-900">Basic Information</h3>
//                   </div>
                  
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     <div className="space-y-3">
//                       <Label htmlFor="name" className="text-sm font-bold text-gray-700 flex items-center gap-2">
//                         <User className="h-4 w-4 text-gray-500" />
//                         Full Name <span className="text-red-500">*</span>
//                       </Label>
//                       <Input
//                         id="name"
//                         value={formData.name}
//                         onChange={(e) => handleInputChange("name", e.target.value)}
//                         placeholder="Enter candidate's full name"
//                         className={getInputClassName("name", "h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}
//                         required
//                       />
//                       {showErrors && errors.name && (
//                         <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                           <X className="h-4 w-4" />
//                           {errors.name}
//                         </p>
//                       )}
//                     </div>
//                     <div className="space-y-3">
//                       <Label htmlFor="fatherName" className="text-sm font-bold text-gray-700 flex items-center gap-2">
//                         <User className="h-4 w-4 text-gray-500" />
//                         Father's Name <span className="text-red-500">*</span>
//                       </Label>
//                       <Input
//                         id="fatherName"
//                         value={formData.fatherName}
//                         onChange={(e) => handleInputChange("fatherName", e.target.value)}
//                         placeholder="Enter father's name"
//                         className={getInputClassName("fatherName", "h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}
//                       />
//                       {showErrors && errors.fatherName && (
//                         <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                           <X className="h-4 w-4" />
//                           {errors.fatherName}
//                         </p>
//                       )}
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     <div className="space-y-3">
//                       <Label htmlFor="email" className="text-sm font-bold text-gray-700 flex items-center gap-2">
//                         <Mail className="h-4 w-4 text-gray-500" />
//                         Email Address <span className="text-red-500">*</span>
//                       </Label>
//                       <Input
//                         id="email"
//                         type="email"
//                         value={formData.email}
//                         onChange={(e) => handleInputChange("email", e.target.value)}
//                         placeholder="candidate@example.com"
//                         className={getInputClassName("email", "h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}
//                         required
//                       />
//                       {showErrors && errors.email && (
//                         <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                           <X className="h-4 w-4" />
//                           {errors.email}
//                         </p>
//                       )}
//                     </div>
//                     <div className="space-y-3">
//                       <Label htmlFor="phone" className="text-sm font-bold text-gray-700 flex items-center gap-2">
//                         <Phone className="h-4 w-4 text-gray-500" />
//                         Phone Number <span className="text-red-500">*</span>
//                       </Label>
//                       <Input
//                         id="phone"
//                         value={formData.phone}
//                         onChange={(e) => handleInputChange("phone", e.target.value)}
//                         placeholder="9876543210"
//                         className={getInputClassName("phone", "h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}
//                         required
//                       />
//                       {showErrors && errors.phone && (
//                         <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                           <X className="h-4 w-4" />
//                           {errors.phone}
//                         </p>
//                       )}
//                     </div>
//                   </div>
//                 </div>

//                 {/* Job Information Section */}
//                 <div className="space-y-6">
//                   <div className="flex items-center gap-3 pb-4 border-b">
//                     <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center">
//                       <Briefcase className="h-4 w-4 text-orange-600" />
//                     </div>
//                     <h3 className="text-md font-bold text-gray-900">Job Information</h3>
//                   </div>

//                   <div className="grid grid-cols-1 gap-6">
//                     <div className="space-y-3">
//                       <Label htmlFor="jobId" className="text-sm font-bold text-gray-700 flex items-center gap-2">
//                         <Target className="h-4 w-4 text-gray-500" />
//                         Job Position <span className="text-red-500">*</span>
//                       </Label>
//                       <div className="relative">
//                         <Select 
//                           value={formData.jobId} 
//                           onValueChange={(value) => {
//                             handleInputChange("jobId", value);
//                             const selectedJob = jobs.find(job => job._id === value);
//                             if (selectedJob) {
//                               console.log('Selected job:', selectedJob);
//                             }
//                           }}
//                           disabled={loadingJobs}
//                         >
//                           <SelectTrigger className={getInputClassName("jobId", "h-12 sm:h-14 rounded-xl border-2 border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/70 font-medium shadow-sm hover:shadow-md transition-all duration-200")}>
//                             <SelectValue placeholder={
//                               loadingJobs ? (
//                                 <div className="flex items-center gap-2">
//                                   <Loader2 className="h-4 w-4 animate-spin" />
//                                   <span>Loading available positions...</span>
//                                 </div>
//                               ) : 
//                               jobsError ? "Error loading jobs - Click to retry" : 
//                               jobs.length === 0 ? "No positions available" :
//                               "Search and select a job position"
//                             } />
//                           </SelectTrigger>
//                           <SelectContent className="rounded-xl max-h-80 border-2 shadow-2xl">
//                             {/* Search Input */}
//                             <div className="p-3 border-b border-gray-200">
//                               <div className="relative">
//                                 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
//                                 <Input
//                                   placeholder="Search jobs by title, company, or location..."
//                                   value={jobSearchTerm}
//                                   onChange={(e) => setJobSearchTerm(e.target.value)}
//                                   className="pl-10 h-10 rounded-lg border-gray-200 focus:border-orange-400 focus:ring-orange-400"
//                                 />
//                               </div>
//                             </div>
                            
//                             {loadingJobs ? (
//                               <div className="flex items-center justify-center p-6">
//                                 <div className="flex flex-col items-center gap-3">
//                                   <Loader2 className="h-6 w-6 animate-spin text-green-600" />
//                                   <span className="text-sm font-medium text-gray-600">Loading positions...</span>
//                                 </div>
//                               </div>
//                             ) : jobsError ? (
//                               <div className="p-6 text-center">
//                                 <div className="flex flex-col gap-3">
//                                   <div className="text-red-600 font-medium">{jobsError}</div>
//                                   <Button
//                                     variant="outline"
//                                     size="sm"
//                                     onClick={() => window.location.reload()}
//                                     className="self-center bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
//                                   >
//                                     🔄 Retry Loading
//                                   </Button>
//                                 </div>
//                               </div>
//                             ) : !Array.isArray(jobs) || jobs.length === 0 ? (
//                               <div className="p-6 text-center text-gray-500">
//                                 <div className="flex flex-col items-center gap-2">
//                                   <Briefcase className="h-8 w-8 text-gray-300" />
//                                   <span className="font-medium">
//                                     {!Array.isArray(jobs) ? 'Invalid job data format' : 'No positions available'}
//                                   </span>
//                                 </div>
//                               </div>
//                             ) : filteredJobs.length === 0 ? (
//                               <div className="p-6 text-center text-gray-500">
//                                 <div className="flex flex-col items-center gap-2">
//                                   <Search className="h-8 w-8 text-gray-300" />
//                                   <span className="font-medium">No jobs match your search</span>
//                                   <span className="text-sm">Try different keywords</span>
//                                 </div>
//                               </div>
//                             ) : (
//                               filteredJobs.map((job, index) => {
//                                 if (!job) {
//                                   console.warn('Invalid job object:', job);
//                                   return null;
//                                 }
                                
//                                 const jobId = job.id || job._id || `job-${index}`;
//                                 const jobTitle = job.title || 'Untitled Job';
                                
//                                 return (
//                                   <SelectItem 
//                                     key={jobId} 
//                                     value={jobId} 
//                                     className="cursor-pointer"
//                                   >
//                                     {jobTitle} - {job.companyName}
//                                   </SelectItem>
//                                 );
//                               }).filter(Boolean)
//                             )}
//                           </SelectContent>
//                         </Select>
//                       </div>
//                       {showErrors && errors.jobId && (
//                         <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                           <X className="h-4 w-4" />
//                           {errors.jobId}
//                         </p>
//                       )}
//                     </div>
//                   </div>
//                 </div>

//                 {/* Personal Details Section */}
//                 <div className="space-y-6">
//                   <div className="flex items-center gap-3 pb-4 border-b">
//                     <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center">
//                       <Calendar className="h-4 w-4 text-orange-600" />
//                     </div>
//                     <h3 className="text-md font-bold text-gray-900">Personal Details</h3>
//                   </div>

//                   <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
//                     <div className="space-y-3">
//                       <Label htmlFor="dateOfBirth" className="text-sm font-bold text-gray-700 flex items-center gap-2">
//                         <Calendar className="h-4 w-4 text-gray-500" />
//                         Date of Birth <span className="text-red-500">*</span>
//                       </Label>
//                       <Input
//                         id="dateOfBirth"
//                         type="date"
//                         value={formData.dateOfBirth}
//                         onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
//                         className={getInputClassName("dateOfBirth", "h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}
//                         max="2009-08-20"
//                         defaultValue="2009-08-20"
//                       />
//                       {showErrors && errors.dateOfBirth && (
//                         <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                           <X className="h-4 w-4" />
//                           {errors.dateOfBirth}
//                         </p>
//                       )}
//                     </div>
//                     <div className="space-y-3">
//                       <Label htmlFor="gender" className="text-sm font-bold text-gray-700">Gender *</Label>
//                       <Select
//                         value={formData.gender}
//                         onValueChange={(value) =>
//                           handleInputChange("gender", value as "Male" | "Female" | "Other")
//                         }
//                       >
//                         <SelectTrigger className="h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium">
//                           <SelectValue placeholder="Select gender" />
//                         </SelectTrigger>
//                         <SelectContent className="rounded-xl">
//                           <SelectItem value="Male">Male</SelectItem>
//                           <SelectItem value="Female">Female</SelectItem>
//                           <SelectItem value="Other">Other</SelectItem>
//                         </SelectContent>
//                       </Select>
//                     </div>
//                     <div className="space-y-3">
//                       <Label htmlFor="aadharNumber" className="text-sm font-bold text-gray-700 flex items-center gap-2">
//                         <FileText className="h-4 w-4 text-gray-500" />
//                         Aadhar Number (optional)
//                       </Label>
//                       <Input
//                         id="aadharNumber"
//                         value={formData.aadharNumber}
//                         onChange={(e) => handleInputChange("aadharNumber", e.target.value)}
//                         placeholder="Enter Aadhar number "
//                         className="h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium"
//                       />
//                     </div>
//                   </div>
//                 </div>

//                 {/* Resume Upload Section */}
//                 <div className="space-y-6">
//                   <div className="flex items-center gap-3 pb-4 border-b">
//                     <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center">
//                       <FileText className="h-4 w-4 text-orange-600" />
//                     </div>
//                     <h3 className="text-md font-bold text-gray-900">Resume Upload</h3>
//                   </div>

//                   <div className="space-y-4">
//                     <Label className="text-sm font-bold text-gray-700 flex items-center gap-2">
//                       <UploadCloud className="h-4 w-4 text-gray-500" />
//                       Resume File
//                     </Label>
//                     <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
//                       <Input
//                         type="file"
//                         ref={fileInputRef}
//                         onChange={handleResumeUpload}
//                         className="hidden"
//                         accept=".pdf,.doc,.docx"
//                       />
//                       <Button
//                         type="button"
//                         variant="outline"
//                         onClick={() => fileInputRef.current?.click()}
//                         className="flex items-center gap-3 h-12 px-6 rounded-xl border-2 border-dashed border-orange-300 hover:border-orange-400 hover:bg-orange-50 transition-all duration-200 w-full sm:w-auto font-medium"
//                       >
//                         <UploadCloud className="h-5 w-5 text-orange-500" />
//                         <span className="truncate">
//                           {resumeFileName ? resumeFileName : "Choose Resume File (Optional)"}
//                         </span>
//                       </Button>
//                       {resumeFileName && (
//                         <Button
//                           type="button"
//                           variant="ghost"
//                           size="sm"
//                           onClick={() => clearResume()}
//                           className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl"
//                         >
//                           <X className="h-4 w-4" />
//                         </Button>
//                       )}
//                     </div>
//                     {resumeFileName && (
//                       <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-xl">
//                         <CheckCircle className="h-5 w-5 text-green-600" />
//                         <span className="text-sm font-medium text-green-800">{resumeFileName} uploaded successfully</span>
//                       </div>
//                     )}
//                   </div>
//                 </div>

//                 {/* Location Details Section */}
//                 <div className="space-y-6">
//                   <div className="flex items-center gap-3 pb-4 border-b">
//                     <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center">
//                       <MapPin className="h-4 w-4 text-orange-600" />
//                     </div>
//                     <h3 className="text-md font-bold text-gray-900">Location Details</h3>
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     <div className="space-y-3">
//                       <Label htmlFor="street" className="text-sm font-bold text-gray-700">Street Address</Label>
//                       <Input
//                         id="street"
//                         value={formData.currentLocationDetails.street}
//                         onChange={(e) => handleInputChange("currentLocationDetails.street", e.target.value)}
//                         placeholder="Enter street address"
//                         className={getInputClassName("currentLocationDetails.street", "h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}
//                       />
//                       {showErrors && errors["currentLocationDetails.street"] && (
//                         <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                           <X className="h-4 w-4" />
//                           {errors["currentLocationDetails.street"]}
//                         </p>
//                       )}
//                     </div>
//                     <div className="space-y-3">
//                       <Label htmlFor="area" className="text-sm font-bold text-gray-700">Area/Locality</Label>
//                       <Input
//                         id="area"
//                         value={formData.currentLocationDetails.area}
//                         onChange={(e) => handleInputChange("currentLocationDetails.area", e.target.value)}
//                         placeholder="Enter area or locality"
//                         className={getInputClassName("currentLocationDetails.area", "h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}
//                       />
//                       {showErrors && errors["currentLocationDetails.area"] && (
//                         <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                           <X className="h-4 w-4" />
//                           {errors["currentLocationDetails.area"]}
//                         </p>
//                       )}
//                     </div>
//                   </div>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     <div className="space-y-3">
//                       <Label htmlFor="city" className="text-sm font-bold text-gray-700 flex items-center gap-2">
//                         <MapPin className="h-4 w-4 text-gray-500" />
//                         City <span className="text-red-500">*</span>
//                       </Label>
//                       <Input
//                         id="city"
//                         value={formData.currentLocationDetails.city}
//                         onChange={(e) => handleInputChange("currentLocationDetails.city", e.target.value)}
//                         placeholder="Enter city name"
//                         className={getInputClassName("city", "h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}
//                         required
//                       />
//                       {showErrors && errors.city && (
//                         <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                           <X className="h-4 w-4" />
//                           {errors.city}
//                         </p>
//                       )}
//                     </div>
//                     <div className="space-y-3">
//                       <Label htmlFor="pincode" className="text-sm font-bold text-gray-700">Pincode</Label>
//                       <Input
//                         id="pincode"
//                         value={formData.currentLocationDetails.pincode}
//                         onChange={(e) => handleInputChange("currentLocationDetails.pincode", e.target.value)}
//                         placeholder="Enter pincode"
//                         className={getInputClassName("currentLocationDetails.pincode", "h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}
//                       />
//                       {showErrors && errors["currentLocationDetails.pincode"] && (
//                         <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                           <X className="h-4 w-4" />
//                           {errors["currentLocationDetails.pincode"]}
//                         </p>
//                       )}
//                     </div>
//                   </div>

//                   {/* <div className="space-y-3">
//                     <Label htmlFor="fullAddress" className="text-sm font-bold text-gray-700">Complete Address</Label>
//                     <Textarea
//                       id="fullAddress"
//                       value={formData.currentLocationDetails.fullAddress}
//                       onChange={(e) => handleInputChange("currentLocationDetails.fullAddress", e.target.value)}
//                       placeholder="Enter complete address with landmarks"
//                       rows={4}
//                       className={getInputClassName("currentLocationDetails.fullAddress", "resize-none rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}
//                     />
//                     {showErrors && errors["currentLocationDetails.fullAddress"] && (
//                       <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                         <X className="h-4 w-4" />
//                         {errors["currentLocationDetails.fullAddress"]}
//                       </p>
//                     )}
//                   </div> */}
//                 </div>

//                 {/* Languages Section */}
//                 <div className="space-y-6">
//                   <div className="flex items-center gap-3 pb-4 border-b">
//                     <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center">
//                       <Languages className="h-4 w-4 text-orange-600" />
//                     </div>
//                     <h3 className="text-md font-bold text-gray-900">Spoken Languages</h3>
//                   </div>

//                   <div className="space-y-4">
//                     <Label className="text-sm font-bold text-gray-700">Select Languages *</Label>
//                     <Select 
//                       value={selectedLanguage}
//                       onValueChange={(value) => {
//                         if (value && !formData.spokenLanguages.includes(value)) {
//                           setFormData((prev) => ({
//                             ...prev,
//                             spokenLanguages: [...prev.spokenLanguages, value],
//                           }));
                          
//                           // Clear the selection after adding
//                           setSelectedLanguage("");
                          
//                           // Clear error when a language is added
//                           if (showErrors && errors.spokenLanguages) {
//                             setErrors((prev) => {
//                               const newErrors = { ...prev };
//                               delete newErrors.spokenLanguages;
//                               return newErrors;
//                             });
//                           }
//                         }
//                       }}>
//                       <SelectTrigger className={getInputClassName("spokenLanguages", "h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}>
//                         <SelectValue placeholder="Select a language to add" />
//                       </SelectTrigger>
//                       <SelectContent className="rounded-xl">
//                         {commonLanguages
//                           .filter(lang => !formData.spokenLanguages.includes(lang))
//                           .map((lang) => (
//                             <SelectItem key={lang} value={lang}>
//                               {lang}
//                             </SelectItem>
//                           ))}
//                       </SelectContent>
//                     </Select>
//                     {showErrors && errors.spokenLanguages && (
//                       <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                         <X className="h-4 w-4" />
//                         {errors.spokenLanguages}
//                       </p>
//                     )}
//                     <div className="flex flex-wrap gap-3 mt-4">
//                       {formData.spokenLanguages.map((language, index) => (
//                         <Badge key={index} variant="secondary" className="gap-2 py-2 px-4 bg-orange-50 text-orange-700 border border-orange-200 rounded-xl font-medium">
//                           {language}
//                           <X
//                             className="h-3 w-3 cursor-pointer hover:text-red-500 transition-colors"
//                             onClick={() => removeFromArray("spokenLanguages", index)}
//                           />
//                         </Badge>
//                       ))}
//                     </div>
//                   </div>
//                 </div>
//               </CardContent>
//             </Card>
//             <Card className="shadow-2xl rounded-3xl border-0 bg-white/80 backdrop-blur-sm overflow-hidden w-full">
//               <CardContent className="p-8 space-y-4 sm:space-y-6 lg:space-y-8">
//                 {/* Professional Information Section */}
//                 <div className="space-y-6">
//                   <div className="flex items-center gap-3 pb-4 border-b">
//                     <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center">
//                       <Award className="h-4 w-4 text-orange-600" />
//                     </div>
//                     <h3 className="text-md font-bold text-gray-900">Educational Background</h3>
//                   </div>

//                   <div className="space-y-6">
//                     <div className="space-y-3">
//                       <Label htmlFor="highestQualification" className="text-sm font-bold text-gray-700 flex items-center gap-2">
//                         <Award className="h-4 w-4 text-gray-500" />
//                         Educational Qualification <span className="text-red-500">*</span>
//                       </Label>
//                       <Select
//                         value={formData.highestQualification}
//                         onValueChange={(value) => handleInputChange("highestQualification", value)}
//                       >
//                         <SelectTrigger className={getInputClassName("highestQualification", "h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}>
//                           <SelectValue placeholder="Select your highest qualification" />
//                         </SelectTrigger>
//                         <SelectContent className="rounded-xl max-h-60">
//                           {qualificationOptions.map((option) => (
//                             <SelectItem key={option.value} value={option.value}>
//                               <div className="flex flex-col">
//                                 <span className="font-medium">{option.label}</span>
//                               </div>
//                             </SelectItem>
//                           ))}
//                         </SelectContent>
//                       </Select>
//                       {showErrors && errors.highestQualification && (
//                         <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                           <X className="h-4 w-4" />
//                           {errors.highestQualification}
//                         </p>
//                       )}
//                     </div>

//                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
//                       <div className="space-y-3">
//                         <Label htmlFor="experienceYears" className="text-sm font-bold text-gray-700 flex items-center gap-2">
//                           <Clock className="h-4 w-4 text-gray-500" />
//                           Work Experience (Years) <span className="text-red-500">*</span>
//                         </Label>
//                         <Input
//                           id="experienceYears"
//                           type="number"
//                           min="0"

//                           value={formData.totalExperienceYears === 0 ? "0" : formData.totalExperienceYears || ""}
//                         onChange={(e) => handleInputChange("totalExperienceYears", e.target.value === "" ? "" : Number(e.target.value))}

//                           className={getInputClassName("totalExperienceYears", "h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}
//                           required
//                         />
//                         {showErrors && errors.totalExperienceYears && (
//                           <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                             <X className="h-4 w-4" />
//                             {errors.totalExperienceYears}
//                           </p>
//                         )}
//                       </div>
//                       <div className="space-y-3">
//                         <Label htmlFor="experienceMonths" className="text-sm font-bold text-gray-700 flex items-center gap-2">
//                           <Clock className="h-4 w-4 text-gray-500" />
//                           Additional Months 
//                         </Label>
//                         <Input
//                           id="experienceMonths"
//                           type="number"
//                           min="0"
//                           max="11"
//                           value={formData.totalExperienceMonths === 0 ? "0" : formData.totalExperienceMonths || ""}
//                           onChange={(e) => handleInputChange("totalExperienceMonths", e.target.value === "" ? "" : Number.parseInt(e.target.value))}
//                           className={getInputClassName("totalExperienceMonths", "h-12 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}
//                         />
//                         {showErrors && errors.totalExperienceMonths && (
//                           <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                             <X className="h-4 w-4" />
//                             {errors.totalExperienceMonths}
//                           </p>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Skills Section */}
//                 <div className="space-y-6">
//                   <div className="flex items-center gap-3 pb-4 border-b">
//                     <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center">
//                       <Target className="h-4 w-4 text-orange-600" />
//                     </div>
//                     <h3 className="text-md font-bold text-gray-900">Skills & Expertise</h3>
//                   </div>

//                   <div className="space-y-4">
//                     <Label className="text-sm font-bold text-gray-700">Technical & Professional Skills</Label>
//                     <div className="flex flex-col sm:flex-row gap-3">
//                       <Input
//                         value={currentSkill}
//                         onChange={(e) => setCurrentSkill(e.target.value)}
//                         placeholder="Add a skill (e.g., React, Python, Digital Marketing, Project Management)"
//                         className={getInputClassName("skills", "h-12 flex-1 rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium")}
//                         onKeyDown={(e) => {
//                           if (e.key === "Enter") {
//                             e.preventDefault();
//                             addToArray("skills", currentSkill, setCurrentSkill);
//                           }
//                         }}
//                       />
//                       <Button
//                         type="button"
//                         variant="outline"
//                         onClick={() => addToArray("skills", currentSkill, setCurrentSkill)}
//                         className="h-12 px-6 rounded-xl border-orange-300 text-orange-600 hover:bg-orange-50 font-medium"
//                       >
//                         <Plus className="h-4 w-4 mr-2" />
//                         Add Skill
//                       </Button>
//                     </div>
//                     <div className="flex flex-wrap gap-3 mt-4">
//                       {formData.skills.map((skill, index) => (
//                         <Badge key={index} variant="secondary" className="gap-2 py-2 px-4 bg-orange-50 text-orange-700 border border-orange-200 rounded-xl font-medium">
//                           {skill}
//                           <X
//                             className="h-3 w-3 cursor-pointer hover:text-red-500 transition-colors"
//                             onClick={() => removeFromArray("skills", index)}
//                           />
//                         </Badge>
//                       ))}
//                     </div>
//                   </div>
//                 </div>

//                 {/* Job Categories Section */}
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   {/* Career Interests Section */}
//                   <div className="space-y-6">
//                     <div className="flex items-center gap-3 pb-4 border-b">
//                        <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center">
//                         <Target className="h-4 w-4 text-orange-600" />
//                       </div>
//                       <h3 className="text-md font-bold text-gray-900">Employment Preferences</h3>
//                     </div>
//                     <div className="space-y-4">
//                       <Label className="text-sm font-bold text-gray-700">Preferred Industries & Job Categories</Label>
//                       <Popover open={openCategoryDropdown} onOpenChange={setOpenCategoryDropdown}>
//                         <PopoverTrigger asChild>
//                           <Button
//                             variant="outline"
//                             role="combobox"
//                             aria-expanded={openCategoryDropdown}
//                             className="h-12 w-full justify-between rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium hover:bg-white/80"
//                           >
//                             <span className="text-gray-500">Select job categories...</span>
//                             <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
//                           </Button>
//                         </PopoverTrigger>
//                         <PopoverContent className="w-full p-0" align="start">
//                           <Command>
//                             <CommandInput placeholder="Search categories..." className="h-9" />
//                             <CommandEmpty>No categories found.</CommandEmpty>
//                             <CommandGroup>
//                               <CommandList>
//                                 {categoryOptions.map((category) => (
//                                   <CommandItem
//                                     key={category}
//                                     value={category}
//                                     onSelect={() => handleCategorySelect(category)}
//                                     className="cursor-pointer"
//                                   >
//                                     <div className="flex items-center justify-between w-full">
//                                       <span>{category}</span>
//                                       {formData.preferredJobCategories.includes(category) && (
//                                         <Check className="h-4 w-4 text-orange-600" />
//                                       )}
//                                     </div>
//                                   </CommandItem>
//                                 ))}
//                               </CommandList>
//                             </CommandGroup>
//                           </Command>
//                         </PopoverContent>
//                       </Popover>
//                       <div className="flex flex-wrap gap-3 mt-4">
//                         {formData.preferredJobCategories.map((category, index) => (
//                           <Badge key={index} variant="secondary" className="gap-2 py-2 px-4 bg-orange-50 text-orange-700 border border-orange-200 rounded-xl font-medium">
//                             {category}
//                             <X
//                               className="h-3 w-3 cursor-pointer hover:text-red-500 transition-colors"
//                               onClick={() => removeFromArray("preferredJobCategories", index)}
//                             />
//                           </Badge>
//                         ))}
//                       </div>
//                     </div>
//                   </div>
//                   {/* Employment Preferences Section */}
//                   <div className="space-y-6 mr-3">
//                     <div className="flex items-center gap-3 pb-4 border-b">
//                       <div className="w-8 h-8 rounded-xl flex items-center justify-center">
                       
//                       </div>
                     
//                     </div>
//                     <div className="space-y-4 sm:space-y-6 lg:space-y-8">
//                       <div className="space-y-4">
//                         <Label className="text-sm font-bold text-gray-700">Preferred Employment Types *</Label>
//                         <Popover open={openEmploymentTypeDropdown} onOpenChange={setOpenEmploymentTypeDropdown}>
//                           <PopoverTrigger asChild>
//                             <Button
//                               variant="outline"
//                               role="combobox"
//                               aria-expanded={openEmploymentTypeDropdown}
//                               className="h-12 w-full justify-between rounded-xl border-gray-200 focus:border-orange-400 focus:ring-orange-400 bg-white/50 font-medium hover:bg-white/80"
//                             >
//                               <span className="text-gray-500">
//                                 {formData.preferredEmploymentTypes.length > 0 
//                                   ? `${formData.preferredEmploymentTypes.length} employment type(s) selected`
//                                   : "Select employment types..."
//                                 }
//                               </span>
//                               <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
//                             </Button>
//                           </PopoverTrigger>
//                           <PopoverContent className="w-full p-0" align="start">
//                             <Command>
//                               <CommandInput placeholder="Search employment types..." className="h-9" />
//                               <CommandEmpty>No employment types found.</CommandEmpty>
//                               <CommandGroup>
//                                 <CommandList>
//                                   {employmentTypeOptions.map((type) => (
//                                     <CommandItem
//                                       key={type}
//                                       value={type}
//                                       onSelect={() => handleEmploymentTypeSelect(type)}
//                                       className="cursor-pointer"
//                                     >
//                                       <div className="flex items-center justify-between w-full">
//                                         <span>{type}</span>
//                                         {formData.preferredEmploymentTypes.includes(type) && (
//                                           <Check className="h-4 w-4 text-orange-600" />
//                                         )}
//                                       </div>
//                                     </CommandItem>
//                                   ))}
//                                 </CommandList>
//                               </CommandGroup>
//                             </Command>
//                           </PopoverContent>
//                         </Popover>
//                         <div className="flex flex-wrap gap-3 mt-4">
//                           {formData.preferredEmploymentTypes.map((type, index) => (
//                             <Badge key={index} variant="secondary" className="gap-2 py-2 px-4 bg-orange-50 text-orange-700 border border-orange-200 rounded-xl font-medium">
//                               {type}
//                               <X
//                                 className="h-3 w-3 cursor-pointer hover:text-red-500 transition-colors"
//                                 onClick={() => removeFromArray("preferredEmploymentTypes", index)}
//                               />
//                             </Badge>
//                           ))}
//                         </div>
//                         {showErrors && errors.preferredEmploymentTypes && (
//                           <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                             <X className="h-4 w-4" />
//                             {errors.preferredEmploymentTypes}
//                           </p>
//                         )}
//                       </div>
                    
//                     </div>
//                   </div>
//                 </div>
//                   <div className="space-y-4">
//                         <Label className="text-sm font-bold text-gray-700">Preferred Work Types *</Label>
//                         <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
//                           {(['remote', 'hybrid', 'on-site'] as const).map((type) => (
//                             <div key={type} className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-xl hover:bg-orange-50 hover:border-orange-300 transition-all duration-200 cursor-pointer">
//                               <Checkbox
//                                 id={type}
//                                 checked={formData.preferredWorkTypes.includes(type)}
//                                 onCheckedChange={(checked) => {
//                                   if (checked) {
//                                     setFormData((prev) => ({
//                                       ...prev,
//                                       preferredWorkTypes: [...prev.preferredWorkTypes, type],
//                                     }));
//                                   } else {
//                                     setFormData((prev) => ({
//                                       ...prev,
//                                       preferredWorkTypes: prev.preferredWorkTypes.filter((t) => t !== type),
//                                     }));
//                                   }
//                                   // Clear error when first checkbox is checked
//                                   if (checked && showErrors && errors.preferredWorkTypes) {
//                                     setErrors((prev) => {
//                                       const newErrors = { ...prev };
//                                       delete newErrors.preferredWorkTypes;
//                                       return newErrors;
//                                     });
//                                   }
//                                 }}
//                                 className="w-6 h-6 rounded border border-gray-300 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500 flex-shrink-0" 
//                                 style={{minHeight: '24px', minWidth: '24px'}}
//                               />
//                               <Label htmlFor={type} className="text-sm font-medium cursor-pointer capitalize flex-1">
//                                 {type.replace("-", " ")}
//                               </Label>
//                             </div>
//                           ))}
//                         </div>
//                         {showErrors && errors.preferredWorkTypes && (
//                           <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
//                             <X className="h-4 w-4" />
//                             {errors.preferredWorkTypes}
//                           </p>
//                         )}
//                       </div>
//               </CardContent>
//             </Card>
          

//           {/* Enhanced Navigation */}
//           <div className="flex justify-between items-center mt-12 pt-8 border-gray-200">
           
//               <Button 
//                 type="submit" 
//                 disabled={isSubmitting}
//                 className="gap-3 px-8 py-4 h-auto rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
//               >
//                 {isSubmitting ? (
//                   <>
//                     <Loader2 className="h-5 w-5 animate-spin" />
//                     Creating Profile...
//                   </>
//                 ) : (
//                   <>
//                     Create Candidate Profile
//                     <Plus className="h-5 w-5" />
//                   </>
//                 )}
//               </Button>
            
//           </div>

//           {/* Enhanced Success Message */}
//           {successMessage && (
//             <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 text-green-800 rounded-2xl flex items-center gap-4 shadow-lg">
//               <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
//                 <CheckCircle className="h-6 w-6 text-white" />
//               </div>
//               <div>
//                 <h4 className="font-bold text-lg">Success!</h4>
//                 <p className="font-medium">{successMessage}</p>
//               </div>
//             </div>
//           )}

//           {/* Loading Overlay */}
//           {isSubmitting && (
//             <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
//               <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md mx-4 text-center">
//                 <div className="w-16 h-12 sm:h-14 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
//                   <Loader2 className="h-8 w-8 text-white animate-spin" />
//                 </div>
//                 <h3 className="text-md font-bold text-gray-900 mb-2">Creating Profile...</h3>
//                 <p className="text-gray-600 mb-4">
//                   We're processing your candidate information and setting up their profile.
//                 </p>
//                 <div className="w-full bg-gray-200 rounded-full h-2">
//                   <div className="bg-gradient-to-r from-orange-500 to-amber-500 h-2 rounded-full animate-pulse" style={{ width: '75%' }}></div>
//                 </div>
//                 <p className="text-sm text-gray-500 mt-3">
//                   You'll be redirected to view candidates shortly...
//                 </p>
//               </div>
//             </div>
//           )}
//         </form>
//       </div>
//     </div>
//   );
// }

